import { z } from 'zod';
import { zTenantPlan, zTenantStatus, zTenantId, TenantBrandingSchema, TenantSettingsSchema, TenantFeaturesSchema, zEntityStatus, zIsoDate, zParentId, zClassId, zStudentId, zSpaceId, zTeacherPermissionKey, zTeacherId, zStaffPermissionKey, zStaffId, zAcademicSessionId, ClassSchema, TeacherSchema, zTenantRole, zMembershipId, zUserId, zTimestamp, roleIdFields, zAnnouncementStatus, zNotificationRecipientRole, zAnnouncementScope, zAnnouncementId, zNotificationId, zNotificationType, PlatformClaimsSchema, UnifiedUserSchema, TenantSchema, UserMembershipSchema, zTenantCode, zKeyProvider, UserProviderKeyViewSchema, UnifiedItemSchema, AnswerKeySchema, zQuestionType, TestSubmissionSchema, StoredEvaluationSchema, DigitalTestSessionSchema, zTestSessionStatus, ChatSessionSchema, SpaceSchema, StoryPointSchema, AgentAssessmentAnswerKeyDataSchema, zItemType, zModelPolicyId, zAgentType, zAgentId, zRubricPresetCategory, UnifiedRubricSchema, zDifficulty, QuestionTypeDataSchema, zBloomsLevel, ChatMessageSchema, ItemPayloadSchema, zJsonValue, StartConversationContextSchema, zConversationMode, ConversationSessionViewSchema, ConversationMessageViewSchema, zConversationSessionId, ConversationTurnViewSchema, ItemSubmissionViewSchema, zSpaceType, zSpaceStatus, zObject, zExamId, zEvaluationSettingsId, zExamQuestionId, zExamStatus, ExamStatsSchema, ExamGradingConfigSchema, zStoryPointId, ExamQuestionPaperSchema, zSectionId, SubQuestionSchema, SubmissionSummarySchema, zSubmissionPipelineStatus, zSubmissionId, GradingProgressSchema, AnswerSheetDataSchema, ScoutingResultSchema, zQuestionGradingStatus, QuestionMappingSchema, zQuestionSubmissionId, ManualOverrideSchema, UnifiedEvaluationResultSchema, EvaluationDisplaySettingsSchema, UsageQuotaConfigSchema, EvaluationConfidenceConfigSchema, EvaluationDimensionSchema, zDeadLetterEntryId, DEAD_LETTER_RESOLUTION_METHODS, GRADING_PIPELINE_STEPS, ScoreDistributionSchema, TopicPerformanceEntrySchema, ClassBreakdownEntrySchema, QuestionAnalyticsEntrySchema, AgentSchema, RubricPresetSchema, StoreSpaceListingSchema, SpaceProgressSchema, StoryPointProgressDocSchema, zConversationSessionStatus, ConversationSessionSummaryViewSchema, AchievementSchema, LeaderboardEntrySchema, StudySessionSchema, SubmissionSchema, ClassProgressSummarySchema, HealthSnapshotSchema, zAtRiskReason, StudentProgressSummarySchema, zInsightId, LearningInsightSchema, zCostSummaryGranularity, DailyCostSummarySchema, MonthlyCostSummarySchema, zPlatformActivityAction, zProgressStatus, ExamAnalyticsSchema, StudentLevelSchema, GamificationSummarySchema, OwnedKeyStatusSchema, NotificationPreferencesSchema, NotificationBadgeStateSchema, NotificationSchema, AcademicSessionSchema, StaffSchema, ParentSchema, StudentSchema, TenantPublicViewSchema, SpaceReviewSchema, QuestionBankItemSchema, ContentVersionSchema, PlatformActivityLogSchema, StudyGoalSchema, StudentAchievementSchema } from '@levelup/domain';
export { ALLOWED_TRANSITIONS, ConversationMessageViewSchema, ConversationSessionSummaryViewSchema, ConversationSessionViewSchema, ConversationTurnViewSchema, ItemSubmissionViewSchema, StartConversationContextSchema, assertTransition, canTransition, zConversationMode, zConversationSessionStatus } from '@levelup/domain';

// src/callable-def.ts
var API_MODULES = ["identity", "levelup", "autograde", "analytics"];
var RATE_TIERS = ["write", "read", "ai", "auth", "report"];
function defineCallable(def) {
  return def;
}
var PageRequest = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(20)
}).strict();
var pageResponse = (item) => z.object({
  items: z.array(item),
  nextCursor: z.string().nullable(),
  // null = end of stream
  total: z.number().int().nonnegative().optional()
}).strict();
var withPaging = (shape) => shape.extend(PageRequest.shape).strict();
var SaveResponseSchema = z.object({
  id: z.string(),
  created: z.boolean().optional(),
  deleted: z.boolean().optional()
}).strict();

// src/callables/identity/_shared.ts
z.object({}).strict();
z.record(z.string(), z.unknown());

// src/callables/identity/tenant.ts
var TenantSummarySchema = z.object({
  id: zTenantId,
  name: z.string(),
  slug: z.string(),
  status: zTenantStatus,
  plan: zTenantPlan,
  totalStudents: z.number().int(),
  totalTeachers: z.number().int(),
  createdAt: z.string()
}).strict();
var SaveTenantRequestSchema = z.object({
  id: zTenantId.optional(),
  data: z.object({
    name: z.string().optional(),
    shortName: z.string().optional(),
    // Public join code (e.g. "SUB001"). On CREATE the server writes the
    // `tenantCodes/{tenantCode}` index so `lookupTenantByCode`/`joinTenant`
    // resolve it; it is the tenant's stable human-facing code (≠ tenantId).
    tenantCode: z.string().optional(),
    slug: z.string().optional(),
    contactEmail: z.string().optional(),
    contactPhone: z.string().optional(),
    plan: zTenantPlan.optional(),
    features: TenantFeaturesSchema.optional(),
    settings: TenantSettingsSchema.optional(),
    branding: TenantBrandingSchema.optional(),
    // Plaintext key on the WIRE only; the server stores a Secret Manager ref
    // (geminiKeyRef) and never persists/returns the value (§authority #8).
    geminiApiKey: z.string().optional()
  }).strict(),
  delete: z.boolean().optional()
}).strict();
var saveTenant = defineCallable({
  name: "v1.identity.saveTenant",
  module: "identity",
  requestSchema: SaveTenantRequestSchema,
  responseSchema: SaveResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["tenants"],
  authoritySensitive: true
});
var DeactivateTenantRequestSchema = z.object({
  tenantOverride: zTenantId,
  reason: z.string().optional()
}).strict();
var DeactivateTenantResponseSchema = z.object({ tenantId: zTenantId, status: zTenantStatus }).strict();
var deactivateTenant = defineCallable({
  name: "v1.identity.deactivateTenant",
  module: "identity",
  requestSchema: DeactivateTenantRequestSchema,
  responseSchema: DeactivateTenantResponseSchema,
  authMode: "authed",
  rateTier: "write",
  allowsTenantOverride: true,
  invalidates: ["tenants", "memberships"],
  authoritySensitive: true
});
var ReactivateTenantRequestSchema = z.object({ tenantOverride: zTenantId }).strict();
var reactivateTenant = defineCallable({
  name: "v1.identity.reactivateTenant",
  module: "identity",
  requestSchema: ReactivateTenantRequestSchema,
  responseSchema: DeactivateTenantResponseSchema,
  authMode: "authed",
  rateTier: "write",
  allowsTenantOverride: true,
  invalidates: ["tenants", "memberships"],
  authoritySensitive: true
});
var EXPORT_COLLECTIONS = [
  "students",
  "teachers",
  "parents",
  "classes",
  "exams",
  "analytics",
  "all"
];
var zExportCollection = z.enum(EXPORT_COLLECTIONS);
var ExportTenantDataRequestSchema = z.object({
  tenantOverride: zTenantId.optional(),
  scope: zExportCollection,
  collections: z.array(zExportCollection).optional()
}).strict();
var ExportTenantDataResponseSchema = z.object({ downloadUrl: z.string(), expiresAt: z.string() }).strict();
var exportTenantData = defineCallable({
  name: "v1.identity.exportTenantData",
  module: "identity",
  requestSchema: ExportTenantDataRequestSchema,
  responseSchema: ExportTenantDataResponseSchema,
  authMode: "authed",
  rateTier: "report",
  allowsTenantOverride: true
});
var ExportJobSchema = z.object({
  id: z.string(),
  scope: zExportCollection,
  status: z.enum(["pending", "running", "completed", "failed", "expired"]),
  downloadUrl: z.string().nullable(),
  requestedAt: z.string(),
  completedAt: z.string().nullable(),
  expiresAt: z.string().nullable()
}).strict();
var ListExportJobsRequestSchema = PageRequest;
var listExportJobs = defineCallable({
  name: "v1.identity.listExportJobs",
  module: "identity",
  requestSchema: ListExportJobsRequestSchema,
  responseSchema: pageResponse(ExportJobSchema),
  authMode: "authed",
  rateTier: "read"
});
var UploadTenantAssetRequestSchema = z.object({
  kind: z.enum(["logo", "banner", "favicon"]),
  contentType: z.string(),
  bytesBase64: z.string()
}).strict();
var UploadTenantAssetResponseSchema = z.object({ assetUrl: z.string() }).strict();
var uploadTenantAsset = defineCallable({
  name: "v1.identity.uploadTenantAsset",
  module: "identity",
  requestSchema: UploadTenantAssetRequestSchema,
  responseSchema: UploadTenantAssetResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["tenants"]
});
var LookupTenantByCodeRequestSchema = z.object({ tenantCode: z.string() }).strict();
var lookupTenantByCode = defineCallable({
  name: "v1.identity.lookupTenantByCode",
  module: "identity",
  requestSchema: LookupTenantByCodeRequestSchema,
  // The ONLY shape returned pre-auth (REVIEW §6.12 — no enumeration leak).
  responseSchema: TenantPublicViewSchema,
  authMode: "public",
  rateTier: "auth"
});
var SaveTenantSettingsRequestSchema = z.object({
  tenantOverride: zTenantId.optional(),
  data: TenantSettingsSchema
}).strict();
var saveTenantSettings = defineCallable({
  name: "v1.identity.saveTenantSettings",
  module: "identity",
  requestSchema: SaveTenantSettingsRequestSchema,
  responseSchema: SaveResponseSchema,
  authMode: "authed",
  rateTier: "write",
  allowsTenantOverride: true,
  invalidates: ["tenants"]
});
var SaveTenantFeaturesRequestSchema = z.object({
  tenantOverride: zTenantId,
  features: TenantFeaturesSchema
}).strict();
var saveTenantFeatures = defineCallable({
  name: "v1.identity.saveTenantFeatures",
  module: "identity",
  requestSchema: SaveTenantFeaturesRequestSchema,
  responseSchema: SaveResponseSchema,
  authMode: "authed",
  rateTier: "write",
  allowsTenantOverride: true,
  invalidates: ["tenants"]
});
var BulkApplyTenantFeaturesRequestSchema = z.object({
  tenantIds: z.array(zTenantId).min(1),
  featureKey: z.enum(["autograde", "levelup", "analytics", "store"]),
  enabled: z.boolean()
}).strict();
var BulkApplyTenantFeaturesResponseSchema = z.object({
  updated: z.number().int(),
  errors: z.array(z.object({ tenantId: zTenantId, error: z.string() }).strict())
}).strict();
var bulkApplyTenantFeatures = defineCallable({
  name: "v1.identity.bulkApplyTenantFeatures",
  module: "identity",
  requestSchema: BulkApplyTenantFeaturesRequestSchema,
  responseSchema: BulkApplyTenantFeaturesResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  // explicit `tenantIds[]` target list (not a single-tenant override) — super-admin.
  invalidates: ["tenants"]
});
var PlatformConfigSchema = z.object({
  trialLength: z.number().int(),
  supportEmail: z.string(),
  branding: TenantBrandingSchema.optional(),
  defaultFeatures: TenantFeaturesSchema,
  maintenanceMode: z.boolean(),
  aiConfigPresent: z.boolean()
}).strict();
var GetPlatformConfigRequestSchema = z.object({}).strict();
var getPlatformConfig = defineCallable({
  name: "v1.identity.getPlatformConfig",
  module: "identity",
  requestSchema: GetPlatformConfigRequestSchema,
  responseSchema: PlatformConfigSchema,
  authMode: "authed",
  rateTier: "read"
});
var SavePlatformConfigRequestSchema = z.object({
  data: z.object({
    trialLength: z.number().int().optional(),
    supportEmail: z.string().optional(),
    branding: TenantBrandingSchema.optional(),
    defaultFeatures: TenantFeaturesSchema.optional(),
    maintenanceMode: z.boolean().optional()
  }).strict()
}).strict();
var SavePlatformConfigResponseSchema = z.object({ saved: z.literal(true) }).strict();
var savePlatformConfig = defineCallable({
  name: "v1.identity.savePlatformConfig",
  module: "identity",
  requestSchema: SavePlatformConfigRequestSchema,
  responseSchema: SavePlatformConfigResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["platformConfig"],
  authoritySensitive: true
});
var GetTenantRequestSchema = z.object({ tenantOverride: zTenantId.optional() }).strict();
var getTenant = defineCallable({
  name: "v1.identity.getTenant",
  module: "identity",
  requestSchema: GetTenantRequestSchema,
  responseSchema: TenantSchema,
  authMode: "authed",
  rateTier: "read",
  allowsTenantOverride: true
});
var ListTenantsRequestSchema = withPaging(
  z.object({
    status: zTenantStatus.optional(),
    plan: zTenantPlan.optional(),
    q: z.string().optional()
  })
);
var listTenants = defineCallable({
  name: "v1.identity.listTenants",
  module: "identity",
  requestSchema: ListTenantsRequestSchema,
  responseSchema: pageResponse(TenantSummarySchema),
  authMode: "authed",
  rateTier: "read"
});
var DAYS_OF_WEEK = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
var zDayOfWeek = z.enum(DAYS_OF_WEEK);
var ClassScheduleSchema = z.object({
  days: z.array(zDayOfWeek).min(1),
  startTime: z.string(),
  endTime: z.string(),
  room: z.string().optional()
}).strict();
var SaveStudentRequestSchema = z.object({
  id: zStudentId.optional(),
  data: z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().optional(),
    rollNumber: z.string().optional(),
    section: z.string().optional(),
    grade: z.string().optional(),
    classIds: z.array(zClassId).optional(),
    parentIds: z.array(zParentId).optional(),
    dateOfBirth: zIsoDate.optional(),
    admissionNumber: z.string().optional(),
    status: zEntityStatus.optional()
  }).strict(),
  delete: z.boolean().optional()
}).strict();
var saveStudent = defineCallable({
  name: "v1.identity.saveStudent",
  module: "identity",
  requestSchema: SaveStudentRequestSchema,
  responseSchema: SaveResponseSchema,
  authMode: "authed",
  rateTier: "write",
  resyncsClaims: true,
  invalidates: ["students", "classes", "memberships", "claims"]
});
var SaveTeacherRequestSchema = z.object({
  id: zTeacherId.optional(),
  data: z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().optional(),
    phone: z.string().optional(),
    subjects: z.array(z.string()).optional(),
    department: z.string().optional(),
    designation: z.string().optional(),
    classIds: z.array(zClassId).optional(),
    permissions: z.object({
      permissions: z.record(zTeacherPermissionKey, z.boolean()).optional(),
      managedSpaceIds: z.array(zSpaceId).optional(),
      managedClassIds: z.array(zClassId).optional()
    }).strict().optional(),
    status: zEntityStatus.optional()
  }).strict(),
  delete: z.boolean().optional()
}).strict();
var saveTeacher = defineCallable({
  name: "v1.identity.saveTeacher",
  module: "identity",
  requestSchema: SaveTeacherRequestSchema,
  responseSchema: SaveResponseSchema,
  authMode: "authed",
  rateTier: "write",
  resyncsClaims: true,
  invalidates: ["teachers", "classes", "memberships", "claims"]
});
var SaveParentRequestSchema = z.object({
  id: zParentId.optional(),
  data: z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().optional(),
    phone: z.string().optional(),
    // canonical parent→child name (D10).
    studentIds: z.array(zStudentId).optional(),
    status: zEntityStatus.optional()
  }).strict(),
  delete: z.boolean().optional()
}).strict();
var saveParent = defineCallable({
  name: "v1.identity.saveParent",
  module: "identity",
  requestSchema: SaveParentRequestSchema,
  responseSchema: SaveResponseSchema,
  authMode: "authed",
  rateTier: "write",
  resyncsClaims: true,
  invalidates: ["parents", "students", "memberships", "claims"]
});
var SaveStaffRequestSchema = z.object({
  id: zStaffId.optional(),
  data: z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().optional(),
    department: z.string().optional(),
    staffPermissions: z.record(zStaffPermissionKey, z.boolean()).optional(),
    status: zEntityStatus.optional()
  }).strict(),
  delete: z.boolean().optional()
}).strict();
var saveStaff = defineCallable({
  name: "v1.identity.saveStaff",
  module: "identity",
  requestSchema: SaveStaffRequestSchema,
  responseSchema: SaveResponseSchema,
  authMode: "authed",
  rateTier: "write",
  resyncsClaims: true,
  invalidates: ["staff", "memberships", "claims"]
});
var SaveClassRequestSchema = z.object({
  id: zClassId.optional(),
  data: z.object({
    name: z.string(),
    grade: z.string(),
    section: z.string().optional(),
    academicSessionId: zAcademicSessionId.optional(),
    teacherIds: z.array(zTeacherId).optional(),
    schedule: ClassScheduleSchema.optional(),
    status: zEntityStatus.optional()
  }).strict(),
  delete: z.boolean().optional()
}).strict();
var saveClass = defineCallable({
  name: "v1.identity.saveClass",
  module: "identity",
  requestSchema: SaveClassRequestSchema,
  responseSchema: SaveResponseSchema,
  authMode: "authed",
  rateTier: "write",
  resyncsClaims: true,
  invalidates: ["classes", "students", "teachers", "claims"]
});
var SaveAcademicSessionRequestSchema = z.object({
  id: zAcademicSessionId.optional(),
  data: z.object({
    name: z.string(),
    startDate: zIsoDate,
    endDate: zIsoDate,
    isCurrent: z.boolean().optional(),
    status: zEntityStatus.optional()
  }).strict(),
  delete: z.boolean().optional()
}).strict();
var saveAcademicSession = defineCallable({
  name: "v1.identity.saveAcademicSession",
  module: "identity",
  requestSchema: SaveAcademicSessionRequestSchema,
  responseSchema: SaveResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["academicSessions", "classes"]
});
var ListStudentsRequestSchema = withPaging(
  z.object({
    classId: zClassId.optional(),
    status: zEntityStatus.optional(),
    q: z.string().optional()
  })
);
var listStudents = defineCallable({
  name: "v1.identity.listStudents",
  module: "identity",
  requestSchema: ListStudentsRequestSchema,
  responseSchema: pageResponse(StudentSchema),
  authMode: "authed",
  rateTier: "read"
});
var GetStudentRequestSchema = z.object({ id: zStudentId }).strict();
var getStudent = defineCallable({
  name: "v1.identity.getStudent",
  module: "identity",
  requestSchema: GetStudentRequestSchema,
  responseSchema: StudentSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListTeachersRequestSchema = withPaging(z.object({ status: zEntityStatus.optional() }));
var listTeachers = defineCallable({
  name: "v1.identity.listTeachers",
  module: "identity",
  requestSchema: ListTeachersRequestSchema,
  responseSchema: pageResponse(TeacherSchema),
  authMode: "authed",
  rateTier: "read"
});
var GetTeacherRequestSchema = z.object({ id: zTeacherId }).strict();
var getTeacher = defineCallable({
  name: "v1.identity.getTeacher",
  module: "identity",
  requestSchema: GetTeacherRequestSchema,
  responseSchema: TeacherSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListParentsRequestSchema = withPaging(z.object({ studentId: zStudentId.optional() }));
var listParents = defineCallable({
  name: "v1.identity.listParents",
  module: "identity",
  requestSchema: ListParentsRequestSchema,
  responseSchema: pageResponse(ParentSchema),
  authMode: "authed",
  rateTier: "read"
});
var ListStaffRequestSchema = PageRequest;
var listStaff = defineCallable({
  name: "v1.identity.listStaff",
  module: "identity",
  requestSchema: ListStaffRequestSchema,
  responseSchema: pageResponse(StaffSchema),
  authMode: "authed",
  rateTier: "read"
});
var ListClassesRequestSchema = withPaging(
  z.object({
    academicSessionId: zAcademicSessionId.optional(),
    status: zEntityStatus.optional()
  })
);
var listClasses = defineCallable({
  name: "v1.identity.listClasses",
  module: "identity",
  requestSchema: ListClassesRequestSchema,
  responseSchema: pageResponse(ClassSchema),
  authMode: "authed",
  rateTier: "read"
});
var ClassDetailViewSchema = z.object({
  class: ClassSchema,
  students: pageResponse(StudentSchema),
  teachers: z.array(TeacherSchema)
}).strict();
var GetClassRequestSchema = z.object({ id: zClassId }).strict();
var getClass = defineCallable({
  name: "v1.identity.getClass",
  module: "identity",
  requestSchema: GetClassRequestSchema,
  responseSchema: ClassDetailViewSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListAcademicSessionsRequestSchema = PageRequest;
var listAcademicSessions = defineCallable({
  name: "v1.identity.listAcademicSessions",
  module: "identity",
  requestSchema: ListAcademicSessionsRequestSchema,
  responseSchema: pageResponse(AcademicSessionSchema),
  authMode: "authed",
  rateTier: "read"
});
var CreateOrgUserRequestSchema = z.object({
  role: zTenantRole,
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().optional(),
  rollNumber: z.string().optional(),
  password: z.string().optional(),
  phone: z.string().optional(),
  classIds: z.array(zClassId).optional(),
  subjects: z.array(z.string()).optional(),
  // canonical parent→child linkage mapped server-side to parentLinkedStudentIds.
  linkedStudentIds: z.array(zStudentId).optional()
}).strict();
var CreateOrgUserResponseSchema = z.object({
  uid: zUserId,
  entityId: z.string(),
  membershipId: zMembershipId
}).strict();
var createOrgUser = defineCallable({
  name: "v1.identity.createOrgUser",
  module: "identity",
  requestSchema: CreateOrgUserRequestSchema,
  responseSchema: CreateOrgUserResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  resyncsClaims: true,
  invalidates: ["students", "teachers", "parents", "staff", "memberships", "claims", "tenants"],
  authoritySensitive: true
});
var SwitchActiveTenantRequestSchema = z.object({ targetTenantId: zTenantId }).strict();
var SwitchActiveTenantResponseSchema = z.object({ tenantId: zTenantId, role: zTenantRole }).strict();
var switchActiveTenant = defineCallable({
  name: "v1.identity.switchActiveTenant",
  module: "identity",
  requestSchema: SwitchActiveTenantRequestSchema,
  responseSchema: SwitchActiveTenantResponseSchema,
  authMode: "authed",
  rateTier: "auth",
  resyncsClaims: true,
  // tenant context changes everything — handled by resetForTenantSwitch (§4.3).
  invalidates: ["me", "claims", "memberships"],
  authoritySensitive: true
});
var JoinTenantRequestSchema = z.object({ tenantCode: z.string() }).strict();
var JoinTenantResponseSchema = z.object({ tenantId: zTenantId, membershipId: zMembershipId, role: zTenantRole }).strict();
var joinTenant = defineCallable({
  name: "v1.identity.joinTenant",
  module: "identity",
  requestSchema: JoinTenantRequestSchema,
  responseSchema: JoinTenantResponseSchema,
  authMode: "authed",
  rateTier: "auth",
  idempotent: true,
  idempotencyKey: "transport",
  resyncsClaims: true,
  invalidates: ["me", "memberships", "claims"]
});
var StudentImportRowSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().optional(),
  rollNumber: z.string().optional(),
  section: z.string().optional(),
  grade: z.string().optional(),
  admissionNumber: z.string().optional(),
  classIds: z.array(zClassId).optional(),
  // Admin-supplied initial password. When present it WINS over server generation
  // (no credential is generated/delivered for the row — the admin already knows it).
  // min(8): reject weak/typo'd credentials at the edge with a clear message rather
  // than a confusing per-row Firebase Auth runtime rejection (stricter than the 6 floor).
  password: z.string().min(8).optional()
}).strict();
var TeacherImportRowSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().optional(),
  phone: z.string().optional(),
  subjects: z.array(z.string()).optional(),
  department: z.string().optional(),
  // Admin-supplied initial password (wins over server generation — see student row).
  password: z.string().min(8).optional()
}).strict();
var BulkRowErrorSchema = z.object({ row: z.number().int(), error: z.string() }).strict();
var BulkRowOutcomeSchema = z.object({
  row: z.number().int(),
  outcome: z.enum(["create", "skip", "error"]),
  error: z.string().optional()
}).strict();
var BulkCredentialsDeliverySchema = z.object({
  /** Signed GET URL to the credentials CSV (deny-all client rules; signed access only). */
  url: z.string().url(),
  /** ISO-8601 expiry of the signed URL (<= 15 min from issue). */
  expiresAt: zTimestamp,
  /** Number of rows whose password was server-generated and included in the CSV. */
  count: z.number().int()
}).strict();
var BulkImportResponseSchema = z.object({
  created: z.number().int(),
  skipped: z.number().int(),
  errors: z.array(BulkRowErrorSchema),
  // Echoed `true` when the request ran as a SIMULATION (zero writes). A dryRun
  // response can therefore never be mistaken for a commit. Absent on a real import.
  dryRun: z.boolean().optional(),
  // Per-row projected outcomes; present ONLY on a dryRun simulation.
  preview: z.array(BulkRowOutcomeSchema).optional(),
  // Signed-URL delivery of server-generated credentials; present ONLY on a real
  // import that generated at least one password. NEVER contains plaintext.
  credentials: BulkCredentialsDeliverySchema.optional()
}).strict();
var BulkImportStudentsRequestSchema = z.object({
  rows: z.array(StudentImportRowSchema).min(1),
  defaultClassIds: z.array(zClassId).optional(),
  // Simulation mode: validate every row + return per-row would-create/would-fail
  // results with ZERO writes (no auth users, no docs, no credentials, no side
  // effects). The response echoes `dryRun: true`.
  dryRun: z.boolean().optional()
}).strict();
var bulkImportStudents = defineCallable({
  name: "v1.identity.bulkImportStudents",
  module: "identity",
  requestSchema: BulkImportStudentsRequestSchema,
  responseSchema: BulkImportResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  resyncsClaims: true,
  invalidates: ["students", "classes", "memberships", "claims", "tenants"],
  authoritySensitive: true
});
var BulkImportTeachersRequestSchema = z.object({
  rows: z.array(TeacherImportRowSchema).min(1),
  // Simulation mode — see `BulkImportStudentsRequestSchema.dryRun`.
  dryRun: z.boolean().optional()
}).strict();
var bulkImportTeachers = defineCallable({
  name: "v1.identity.bulkImportTeachers",
  module: "identity",
  requestSchema: BulkImportTeachersRequestSchema,
  responseSchema: BulkImportResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  resyncsClaims: true,
  invalidates: ["teachers", "memberships", "claims", "tenants"],
  authoritySensitive: true
});
var BulkUpdateStatusRequestSchema = z.object({
  entityType: z.enum(["student", "teacher", "class"]),
  ids: z.array(z.string()).min(1),
  status: zEntityStatus
}).strict();
var BulkUpdateStatusResponseSchema = z.object({
  updated: z.number().int(),
  errors: z.array(z.object({ id: z.string(), error: z.string() }).strict())
}).strict();
var bulkUpdateStatus = defineCallable({
  name: "v1.identity.bulkUpdateStatus",
  module: "identity",
  requestSchema: BulkUpdateStatusRequestSchema,
  responseSchema: BulkUpdateStatusResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  resyncsClaims: true,
  invalidates: ["students", "teachers", "memberships", "claims"],
  authoritySensitive: true
});
var ChangeMembershipRoleRequestSchema = z.object({
  uid: zUserId,
  toRole: zTenantRole,
  // DP-2 Part B: the per-role id links are DERIVED from `ID_ROLES` (one source) —
  // this auto-includes `scannerId`, fixing B-IDN-23 (scanner was orphaned here).
  links: z.object(roleIdFields).strict().optional()
}).strict();
var ChangeMembershipRoleResponseSchema = z.object({ membershipId: zMembershipId, role: zTenantRole }).strict();
var changeMembershipRole = defineCallable({
  name: "v1.identity.changeMembershipRole",
  module: "identity",
  requestSchema: ChangeMembershipRoleRequestSchema,
  responseSchema: ChangeMembershipRoleResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  resyncsClaims: true,
  invalidates: ["memberships", "claims"],
  authoritySensitive: true
});
var RolloverSessionRequestSchema = z.object({
  fromSessionId: zAcademicSessionId,
  toSessionId: zAcademicSessionId,
  // optional class→class promotion mapping (old classId → new classId).
  promotionMap: z.record(zClassId, zClassId).optional()
}).strict();
var RolloverSessionResponseSchema = z.object({ classesCreated: z.number().int(), studentsMoved: z.number().int() }).strict();
var rolloverSession = defineCallable({
  name: "v1.identity.rolloverSession",
  module: "identity",
  requestSchema: RolloverSessionRequestSchema,
  responseSchema: RolloverSessionResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  invalidates: ["classes", "academicSessions", "students"]
});
var SaveAnnouncementRequestSchema = z.object({
  id: zAnnouncementId.optional(),
  // platform scope is super-admin; tenant scope is admin. `tenantOverride` is
  // the super-admin cross-tenant field (never a body `tenantId`).
  tenantOverride: zTenantId.optional(),
  data: z.object({
    scope: zAnnouncementScope.optional(),
    title: z.string().max(200).optional(),
    body: z.string().max(5e3).optional(),
    targetRoles: z.array(zNotificationRecipientRole).max(10).optional(),
    targetClassIds: z.array(zClassId).max(100).optional(),
    status: zAnnouncementStatus.optional(),
    expiresAt: z.string().optional()
  }).strict(),
  delete: z.boolean().optional()
}).strict();
var saveAnnouncement = defineCallable({
  name: "v1.identity.saveAnnouncement",
  module: "identity",
  requestSchema: SaveAnnouncementRequestSchema,
  responseSchema: SaveResponseSchema,
  authMode: "authed",
  rateTier: "write",
  allowsTenantOverride: true,
  invalidates: ["announcements"],
  // publish lifecycle transition (assertTransition(announcement)) — never optimistic.
  authoritySensitive: true
});
var AnnouncementListItemSchema = z.object({
  id: zAnnouncementId,
  title: z.string(),
  body: z.string(),
  scope: zAnnouncementScope,
  status: zAnnouncementStatus,
  authorName: z.string(),
  publishedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  isReadByMe: z.boolean()
}).strict();
var ListAnnouncementsRequestSchema = withPaging(
  z.object({
    scope: zAnnouncementScope.optional(),
    status: zAnnouncementStatus.optional()
  })
);
var listAnnouncements = defineCallable({
  name: "v1.identity.listAnnouncements",
  module: "identity",
  requestSchema: ListAnnouncementsRequestSchema,
  responseSchema: pageResponse(AnnouncementListItemSchema),
  authMode: "authed",
  rateTier: "read"
});
var MarkAnnouncementReadRequestSchema = z.object({ announcementId: zAnnouncementId }).strict();
var MarkAnnouncementReadResponseSchema = z.object({ isReadByMe: z.literal(true) }).strict();
var markAnnouncementRead = defineCallable({
  name: "v1.identity.markAnnouncementRead",
  module: "identity",
  requestSchema: MarkAnnouncementReadRequestSchema,
  responseSchema: MarkAnnouncementReadResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "domain:announcementId",
  invalidates: ["announcements"]
});
var EstimateAudienceRequestSchema = z.object({
  targetRoles: z.array(zNotificationRecipientRole).max(10).optional(),
  targetClassIds: z.array(zClassId).max(100).optional()
}).strict();
var EstimateAudienceResponseSchema = z.object({ recipientCount: z.number().int() }).strict();
var estimateAudience = defineCallable({
  name: "v1.identity.estimateAudience",
  module: "identity",
  requestSchema: EstimateAudienceRequestSchema,
  responseSchema: EstimateAudienceResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListNotificationsRequestSchema = PageRequest;
var listNotifications = defineCallable({
  name: "v1.identity.listNotifications",
  module: "identity",
  requestSchema: ListNotificationsRequestSchema,
  responseSchema: pageResponse(NotificationSchema),
  authMode: "authed",
  rateTier: "read"
});
var GetNotificationBadgeRequestSchema = z.object({}).strict();
var getNotificationBadge = defineCallable({
  name: "v1.identity.getNotificationBadge",
  module: "identity",
  requestSchema: GetNotificationBadgeRequestSchema,
  responseSchema: NotificationBadgeStateSchema,
  authMode: "authed",
  rateTier: "read"
});
var MarkNotificationReadRequestSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("one"), notificationId: zNotificationId }).strict(),
  z.object({ mode: z.literal("all") }).strict()
]);
var MarkNotificationReadResponseSchema = z.object({ unreadCount: z.number().int().min(0) }).strict();
var markNotificationRead = defineCallable({
  name: "v1.identity.markNotificationRead",
  module: "identity",
  requestSchema: MarkNotificationReadRequestSchema,
  responseSchema: MarkNotificationReadResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  invalidates: ["notifications", "notificationBadge"]
});
var GetNotificationPreferencesRequestSchema = z.object({}).strict();
var getNotificationPreferences = defineCallable({
  name: "v1.identity.getNotificationPreferences",
  module: "identity",
  requestSchema: GetNotificationPreferencesRequestSchema,
  responseSchema: NotificationPreferencesSchema,
  authMode: "authed",
  rateTier: "read"
});
var SaveNotificationPreferencesRequestSchema = z.object({
  enabledTypes: z.array(zNotificationType).optional(),
  muteUntil: z.string().nullable().optional()
}).strict();
var saveNotificationPreferences = defineCallable({
  name: "v1.identity.saveNotificationPreferences",
  module: "identity",
  requestSchema: SaveNotificationPreferencesRequestSchema,
  responseSchema: NotificationPreferencesSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  invalidates: ["notificationPreferences"]
});
var RegisterDeviceTokenRequestSchema = z.object({
  token: z.string(),
  platform: z.enum(["ios", "android", "web"]),
  appKey: z.string()
}).strict();
var DeviceTokenAckSchema = z.object({ ok: z.literal(true) }).strict();
var registerDeviceToken = defineCallable({
  name: "v1.identity.registerDeviceToken",
  module: "identity",
  requestSchema: RegisterDeviceTokenRequestSchema,
  responseSchema: DeviceTokenAckSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "domain:token",
  invalidates: ["device"]
});
var UnregisterDeviceTokenRequestSchema = z.object({ token: z.string() }).strict();
var unregisterDeviceToken = defineCallable({
  name: "v1.identity.unregisterDeviceToken",
  module: "identity",
  requestSchema: UnregisterDeviceTokenRequestSchema,
  responseSchema: DeviceTokenAckSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "domain:token",
  invalidates: ["device"]
});
var SendDirectMessageRequestSchema = z.object({
  recipientUids: z.array(zUserId).min(1),
  title: z.string().max(200),
  body: z.string().max(5e3)
}).strict();
var SendDirectMessageResponseSchema = z.object({ sent: z.literal(true), count: z.number().int() }).strict();
var sendDirectMessage = defineCallable({
  name: "v1.identity.sendDirectMessage",
  module: "identity",
  requestSchema: SendDirectMessageRequestSchema,
  responseSchema: SendDirectMessageResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["message"]
});
var GetMeRequestSchema = z.object({}).strict();
var GetMeResponseSchema = z.object({
  user: UnifiedUserSchema,
  memberships: z.array(UserMembershipSchema),
  claims: PlatformClaimsSchema,
  activeTenant: TenantSchema.optional()
}).strict();
var getMe = defineCallable({
  name: "v1.identity.getMe",
  module: "identity",
  requestSchema: GetMeRequestSchema,
  responseSchema: GetMeResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var UpdateMyProfileRequestSchema = z.object({
  displayName: z.string().optional(),
  photoURL: z.string().optional()
}).strict();
var OkResponseSchema = z.object({ ok: z.literal(true) }).strict();
var updateMyProfile = defineCallable({
  name: "v1.identity.updateMyProfile",
  module: "identity",
  requestSchema: UpdateMyProfileRequestSchema,
  responseSchema: OkResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["me"]
});
var UploadUserAssetRequestSchema = z.object({
  kind: z.literal("avatar"),
  contentType: z.string(),
  bytesBase64: z.string()
}).strict();
var UploadUserAssetResponseSchema = z.object({ assetUrl: z.string() }).strict();
var uploadUserAsset = defineCallable({
  name: "v1.identity.uploadUserAsset",
  module: "identity",
  requestSchema: UploadUserAssetRequestSchema,
  responseSchema: UploadUserAssetResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["me"]
});
var DeleteConsumerAccountRequestSchema = z.object({ confirm: z.literal(true) }).strict();
var DeleteConsumerAccountResponseSchema = z.object({ scheduled: z.literal(true) }).strict();
var deleteConsumerAccount = defineCallable({
  name: "v1.identity.deleteConsumerAccount",
  module: "identity",
  requestSchema: DeleteConsumerAccountRequestSchema,
  responseSchema: DeleteConsumerAccountResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["me"],
  authoritySensitive: true
});
var UserSearchResultSchema = z.object({
  uid: zUserId,
  email: z.string().optional(),
  displayName: z.string(),
  isSuperAdmin: z.boolean(),
  activeTenantId: zTenantId.optional(),
  memberships: z.array(
    z.object({ tenantId: zTenantId, tenantCode: zTenantCode, role: zTenantRole }).strict()
  )
}).strict();
var SearchUsersRequestSchema = withPaging(z.object({ query: z.string() }));
var searchUsers = defineCallable({
  name: "v1.identity.searchUsers",
  module: "identity",
  requestSchema: SearchUsersRequestSchema,
  responseSchema: pageResponse(UserSearchResultSchema),
  authMode: "authed",
  rateTier: "read"
});
var GlobalEvaluationPresetSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  category: z.string().optional(),
  status: z.enum(["active", "archived"]),
  rubricSnapshot: z.unknown(),
  createdAt: z.string(),
  updatedAt: z.string()
}).strict();
var SaveGlobalEvaluationPresetRequestSchema = z.object({
  id: z.string().optional(),
  data: z.object({
    name: z.string(),
    description: z.string().optional(),
    category: z.string().optional(),
    status: z.enum(["active", "archived"]).optional(),
    rubricSnapshot: z.unknown().optional()
  }).strict(),
  delete: z.boolean().optional()
}).strict();
var saveGlobalEvaluationPreset = defineCallable({
  name: "v1.identity.saveGlobalEvaluationPreset",
  module: "identity",
  requestSchema: SaveGlobalEvaluationPresetRequestSchema,
  responseSchema: SaveResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["preset"]
});
var ListGlobalEvaluationPresetsRequestSchema = withPaging(
  z.object({ status: z.enum(["active", "archived"]).optional() })
);
var listGlobalEvaluationPresets = defineCallable({
  name: "v1.identity.listGlobalEvaluationPresets",
  module: "identity",
  requestSchema: ListGlobalEvaluationPresetsRequestSchema,
  responseSchema: pageResponse(GlobalEvaluationPresetSchema),
  authMode: "authed",
  rateTier: "read"
});
var SetUserStatusRequestSchema = z.object({
  uid: zUserId,
  status: z.enum(["disabled", "active"])
}).strict();
var SetUserStatusResponseSchema = z.object({ uid: zUserId, status: z.enum(["disabled", "active"]) }).strict();
var setUserStatus = defineCallable({
  name: "v1.identity.setUserStatus",
  module: "identity",
  requestSchema: SetUserStatusRequestSchema,
  responseSchema: SetUserStatusResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["userSearch"],
  authoritySensitive: true
});
var SendPasswordResetRequestSchema = z.object({ uid: zUserId }).strict();
var SendPasswordResetResponseSchema = z.object({ sent: z.literal(true) }).strict();
var sendPasswordReset = defineCallable({
  name: "v1.identity.sendPasswordReset",
  module: "identity",
  requestSchema: SendPasswordResetRequestSchema,
  responseSchema: SendPasswordResetResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["userSearch"],
  authoritySensitive: true
});
var StartImpersonationRequestSchema = z.object({
  targetUid: zUserId,
  // super-admin cross-tenant field (never a body `tenantId`).
  tenantOverride: zTenantId,
  reason: z.string()
}).strict();
var StartImpersonationResponseSchema = z.object({ sessionToken: z.string(), expiresAt: z.string() }).strict();
var startImpersonation = defineCallable({
  name: "v1.identity.startImpersonation",
  module: "identity",
  requestSchema: StartImpersonationRequestSchema,
  responseSchema: StartImpersonationResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  allowsTenantOverride: true,
  invalidates: ["userSearch"],
  authoritySensitive: true
});
var EndImpersonationRequestSchema = z.object({}).strict();
var EndImpersonationResponseSchema = z.object({ ended: z.literal(true) }).strict();
var endImpersonation = defineCallable({
  name: "v1.identity.endImpersonation",
  module: "identity",
  requestSchema: EndImpersonationRequestSchema,
  responseSchema: EndImpersonationResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  invalidates: ["userSearch"],
  authoritySensitive: true
});
var SaveUserProviderKeyRequestSchema = z.object({
  provider: zKeyProvider,
  // Plaintext ONLY on the wire; validated + stored in Secret Manager server-side.
  apiKey: z.string().min(1),
  label: z.string().optional(),
  enabled: z.boolean().optional()
}).strict();
var saveUserProviderKey = defineCallable({
  name: "v1.identity.saveUserProviderKey",
  module: "identity",
  requestSchema: SaveUserProviderKeyRequestSchema,
  responseSchema: UserProviderKeyViewSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["userProviderKeys"],
  authoritySensitive: true
});
var ListUserProviderKeysRequestSchema = z.object({}).strict();
var ListUserProviderKeysResponseSchema = z.object({ keys: z.array(UserProviderKeyViewSchema) }).strict();
var listUserProviderKeys = defineCallable({
  name: "v1.identity.listUserProviderKeys",
  module: "identity",
  requestSchema: ListUserProviderKeysRequestSchema,
  responseSchema: ListUserProviderKeysResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var SetUserProviderKeyEnabledRequestSchema = z.object({ provider: zKeyProvider, enabled: z.boolean() }).strict();
var setUserProviderKeyEnabled = defineCallable({
  name: "v1.identity.setUserProviderKeyEnabled",
  module: "identity",
  requestSchema: SetUserProviderKeyEnabledRequestSchema,
  responseSchema: UserProviderKeyViewSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["userProviderKeys"]
});
var DeleteUserProviderKeyRequestSchema = z.object({ provider: zKeyProvider }).strict();
var deleteUserProviderKey = defineCallable({
  name: "v1.identity.deleteUserProviderKey",
  module: "identity",
  requestSchema: DeleteUserProviderKeyRequestSchema,
  responseSchema: SaveResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["userProviderKeys"],
  authoritySensitive: true
});
var RotateTenantKeyRequestSchema = z.object({
  tenantOverride: z.string().optional(),
  provider: zKeyProvider,
  apiKey: z.string().min(1)
}).strict();
var rotateTenantKey = defineCallable({
  name: "v1.identity.rotateTenantKey",
  module: "identity",
  requestSchema: RotateTenantKeyRequestSchema,
  responseSchema: OwnedKeyStatusSchema,
  authMode: "authed",
  rateTier: "write",
  allowsTenantOverride: true,
  invalidates: ["tenantKeys", "tenants"],
  authoritySensitive: true
});
var RevokeTenantKeyRequestSchema = z.object({ tenantOverride: z.string().optional(), provider: zKeyProvider }).strict();
var revokeTenantKey = defineCallable({
  name: "v1.identity.revokeTenantKey",
  module: "identity",
  requestSchema: RevokeTenantKeyRequestSchema,
  responseSchema: SaveResponseSchema,
  authMode: "authed",
  rateTier: "write",
  allowsTenantOverride: true,
  invalidates: ["tenantKeys", "tenants"],
  authoritySensitive: true
});
var GetTenantKeyStatusRequestSchema = z.object({ tenantOverride: z.string().optional(), provider: zKeyProvider.optional() }).strict();
var getTenantKeyStatus = defineCallable({
  name: "v1.identity.getTenantKeyStatus",
  module: "identity",
  requestSchema: GetTenantKeyStatusRequestSchema,
  responseSchema: OwnedKeyStatusSchema,
  authMode: "authed",
  rateTier: "read",
  allowsTenantOverride: true
});
var SavePlatformKeyRequestSchema = z.object({ provider: zKeyProvider, apiKey: z.string().min(1) }).strict();
var savePlatformKey = defineCallable({
  name: "v1.identity.savePlatformKey",
  module: "identity",
  requestSchema: SavePlatformKeyRequestSchema,
  responseSchema: OwnedKeyStatusSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["platformKeys"],
  authoritySensitive: true
});
var GetPlatformKeyStatusRequestSchema = z.object({ provider: zKeyProvider.optional() }).strict();
var getPlatformKeyStatus = defineCallable({
  name: "v1.identity.getPlatformKeyStatus",
  module: "identity",
  requestSchema: GetPlatformKeyStatusRequestSchema,
  responseSchema: OwnedKeyStatusSchema,
  authMode: "authed",
  rateTier: "read"
});

// src/callables/identity/index.ts
var IDENTITY_CALLABLES = {
  // tenant lifecycle + settings/features + platform config
  "v1.identity.saveTenant": saveTenant,
  "v1.identity.deactivateTenant": deactivateTenant,
  "v1.identity.reactivateTenant": reactivateTenant,
  "v1.identity.exportTenantData": exportTenantData,
  "v1.identity.listExportJobs": listExportJobs,
  "v1.identity.uploadTenantAsset": uploadTenantAsset,
  "v1.identity.lookupTenantByCode": lookupTenantByCode,
  "v1.identity.saveTenantSettings": saveTenantSettings,
  "v1.identity.saveTenantFeatures": saveTenantFeatures,
  "v1.identity.bulkApplyTenantFeatures": bulkApplyTenantFeatures,
  "v1.identity.getPlatformConfig": getPlatformConfig,
  "v1.identity.savePlatformConfig": savePlatformConfig,
  "v1.identity.getTenant": getTenant,
  "v1.identity.listTenants": listTenants,
  // org-entity upserts + reads
  "v1.identity.saveStudent": saveStudent,
  "v1.identity.saveTeacher": saveTeacher,
  "v1.identity.saveParent": saveParent,
  "v1.identity.saveStaff": saveStaff,
  "v1.identity.saveClass": saveClass,
  "v1.identity.saveAcademicSession": saveAcademicSession,
  "v1.identity.listStudents": listStudents,
  "v1.identity.getStudent": getStudent,
  "v1.identity.listTeachers": listTeachers,
  "v1.identity.getTeacher": getTeacher,
  "v1.identity.listParents": listParents,
  "v1.identity.listStaff": listStaff,
  "v1.identity.listClasses": listClasses,
  "v1.identity.getClass": getClass,
  "v1.identity.listAcademicSessions": listAcademicSessions,
  // multi-tenant user management + bulk ops
  "v1.identity.createOrgUser": createOrgUser,
  "v1.identity.switchActiveTenant": switchActiveTenant,
  "v1.identity.joinTenant": joinTenant,
  "v1.identity.bulkImportStudents": bulkImportStudents,
  "v1.identity.bulkImportTeachers": bulkImportTeachers,
  "v1.identity.bulkUpdateStatus": bulkUpdateStatus,
  "v1.identity.changeMembershipRole": changeMembershipRole,
  "v1.identity.rolloverSession": rolloverSession,
  // announcements + notifications + preferences + device tokens
  "v1.identity.saveAnnouncement": saveAnnouncement,
  "v1.identity.listAnnouncements": listAnnouncements,
  "v1.identity.markAnnouncementRead": markAnnouncementRead,
  "v1.identity.estimateAudience": estimateAudience,
  "v1.identity.listNotifications": listNotifications,
  "v1.identity.getNotificationBadge": getNotificationBadge,
  "v1.identity.markNotificationRead": markNotificationRead,
  "v1.identity.getNotificationPreferences": getNotificationPreferences,
  "v1.identity.saveNotificationPreferences": saveNotificationPreferences,
  "v1.identity.registerDeviceToken": registerDeviceToken,
  "v1.identity.unregisterDeviceToken": unregisterDeviceToken,
  "v1.identity.sendDirectMessage": sendDirectMessage,
  // session/profile reads + account self-service + super-admin platform ops
  "v1.identity.getMe": getMe,
  "v1.identity.updateMyProfile": updateMyProfile,
  "v1.identity.uploadUserAsset": uploadUserAsset,
  "v1.identity.deleteConsumerAccount": deleteConsumerAccount,
  "v1.identity.searchUsers": searchUsers,
  "v1.identity.saveGlobalEvaluationPreset": saveGlobalEvaluationPreset,
  "v1.identity.listGlobalEvaluationPresets": listGlobalEvaluationPresets,
  "v1.identity.setUserStatus": setUserStatus,
  "v1.identity.sendPasswordReset": sendPasswordReset,
  "v1.identity.startImpersonation": startImpersonation,
  "v1.identity.endImpersonation": endImpersonation,
  // API key management — per-user BYOK, tenant keys, platform fallback keys
  "v1.identity.saveUserProviderKey": saveUserProviderKey,
  "v1.identity.listUserProviderKeys": listUserProviderKeys,
  "v1.identity.setUserProviderKeyEnabled": setUserProviderKeyEnabled,
  "v1.identity.deleteUserProviderKey": deleteUserProviderKey,
  "v1.identity.rotateTenantKey": rotateTenantKey,
  "v1.identity.revokeTenantKey": revokeTenantKey,
  "v1.identity.getTenantKeyStatus": getTenantKeyStatus,
  "v1.identity.savePlatformKey": savePlatformKey,
  "v1.identity.getPlatformKeyStatus": getPlatformKeyStatus
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
};
var SaveOrDeleteResponseSchema = z.union([
  SaveResponseSchema,
  z.object({ id: z.string(), deleted: z.literal(true) }).strict()
]);
var SpaceViewSchema = SpaceSchema;
var StoryPointViewSchema = StoryPointSchema;
var ItemViewSchema = UnifiedItemSchema;
var ItemEditViewSchema = UnifiedItemSchema.extend({
  // The ONE sanctioned answer-bearing read: getItemForEdit re-merges the ⚷ AnswerKey.
  answerKey: AnswerKeySchema.optional()
});
var DigitalTestSessionViewSchema = DigitalTestSessionSchema;
var TestSubmissionResultViewSchema = z.object({
  itemId: TestSubmissionSchema.shape.itemId,
  questionType: zQuestionType,
  answer: z.unknown(),
  correct: z.boolean().nullable(),
  pointsEarned: z.number().nullable(),
  totalPoints: z.number().nullable(),
  evaluation: StoredEvaluationSchema.nullable(),
  pending: z.boolean()
}).strict();
var DigitalTestSessionResultViewSchema = DigitalTestSessionSchema.extend({
  submissions: z.array(TestSubmissionResultViewSchema).default([])
}).strict();
var DigitalTestSessionSummaryViewSchema = z.object({
  id: DigitalTestSessionSchema.shape.id,
  spaceId: DigitalTestSessionSchema.shape.spaceId,
  storyPointId: DigitalTestSessionSchema.shape.storyPointId,
  sessionType: DigitalTestSessionSchema.shape.sessionType,
  status: zTestSessionStatus,
  attemptNumber: z.number().int(),
  isLatest: z.boolean(),
  percentage: z.number().optional(),
  startedAt: DigitalTestSessionSchema.shape.startedAt,
  submittedAt: DigitalTestSessionSchema.shape.submittedAt
}).strict();
var SpaceProgressViewSchema = SpaceProgressSchema;
var StoryPointProgressDocViewSchema = StoryPointProgressDocSchema;
var ItemProgressViewSchema = z.object({
  itemId: z.string(),
  completed: z.boolean(),
  bestScore: z.number().optional(),
  latestScore: z.number().optional(),
  pointsEarned: z.number().optional(),
  totalPoints: z.number().optional(),
  percentage: z.number().optional(),
  attemptsCount: z.number().int().optional(),
  solved: z.boolean().optional(),
  evaluation: StoredEvaluationSchema.nullable()
}).strict();
var AgentViewSchema = AgentSchema;
var ChatSessionSummarySchema = z.object({
  id: ChatSessionSchema.shape.id,
  spaceId: ChatSessionSchema.shape.spaceId,
  storyPointId: ChatSessionSchema.shape.storyPointId,
  itemId: ChatSessionSchema.shape.itemId,
  sessionTitle: z.string(),
  previewMessage: z.string(),
  messageCount: z.number().int(),
  language: z.string(),
  isActive: z.boolean(),
  updatedAt: ChatSessionSchema.shape.updatedAt
}).strict();
var ChatSessionViewSchema = ChatSessionSchema.omit({ systemPrompt: true }).strict();

// src/callables/levelup/save-space.ts
var SaveSpaceDataSchema = SpaceSchema.pick({
  title: true,
  type: true,
  description: true,
  thumbnailUrl: true,
  slug: true,
  subject: true,
  labels: true,
  classIds: true,
  sectionIds: true,
  teacherIds: true,
  accessType: true,
  academicSessionId: true,
  defaultEvaluatorAgentId: true,
  defaultTutorAgentId: true,
  defaultRubric: true,
  defaultRubricId: true,
  evaluationSettingsId: true,
  allowRetakes: true,
  maxRetakes: true,
  defaultTimeLimitMinutes: true,
  showCorrectAnswers: true,
  status: true,
  publishedToStore: true,
  price: true,
  storeDescription: true,
  storeThumbnailUrl: true
}).partial().extend({
  deleted: z.boolean().optional()
}).strict();
var SaveSpaceRequestSchema = z.object({
  id: SpaceSchema.shape.id.optional(),
  data: SaveSpaceDataSchema
}).strict();
var SaveSpaceResponseSchema = SaveResponseSchema;
var saveSpaceDef = defineCallable({
  name: "v1.levelup.saveSpace",
  module: "levelup",
  requestSchema: SaveSpaceRequestSchema,
  responseSchema: SaveSpaceResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["spaces", "store"],
  authoritySensitive: true
});
var DuplicateSpaceRequestSchema = z.object({
  spaceId: z.string().min(1)
}).strict();
var DuplicateSpaceResponseSchema = SaveResponseSchema;
var duplicateSpaceDef = defineCallable({
  name: "v1.levelup.duplicateSpace",
  module: "levelup",
  requestSchema: DuplicateSpaceRequestSchema,
  responseSchema: DuplicateSpaceResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["spaces"],
  authoritySensitive: false
});
var SaveStoryPointDataSchema = StoryPointSchema.pick({
  title: true,
  description: true,
  orderIndex: true,
  type: true,
  sections: true,
  assessmentConfig: true,
  defaultRubric: true,
  defaultRubricId: true,
  difficulty: true,
  estimatedTimeMinutes: true
}).partial().extend({
  deleted: z.boolean().optional()
}).strict();
var SaveStoryPointRequestSchema = z.object({
  id: StoryPointSchema.shape.id.optional(),
  spaceId: StoryPointSchema.shape.spaceId,
  data: SaveStoryPointDataSchema
}).strict();
var SaveStoryPointResponseSchema = SaveOrDeleteResponseSchema;
var saveStoryPointDef = defineCallable({
  name: "v1.levelup.saveStoryPoint",
  module: "levelup",
  requestSchema: SaveStoryPointRequestSchema,
  responseSchema: SaveStoryPointResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["storyPoints", "spaces"],
  authoritySensitive: true
});
var SaveItemDataSchema = UnifiedItemSchema.pick({
  type: true,
  payload: true,
  title: true,
  content: true,
  difficulty: true,
  topics: true,
  labels: true,
  orderIndex: true,
  sectionId: true,
  meta: true,
  rubric: true,
  rubricId: true,
  linkedQuestionId: true,
  attachments: true
}).partial().extend({
  /**
   * Private chat-agent assessment data. It is written into the deny-all
   * answer-key document by the service and never appears in learner ItemView.
   */
  answerKey: AgentAssessmentAnswerKeyDataSchema.optional(),
  deleted: z.boolean().optional()
}).strict().superRefine((data, ctx) => {
  if (data.type !== void 0 && data.payload !== void 0 && data.type !== data.payload.type) {
    ctx.addIssue({
      code: "custom",
      message: "type must match payload.type",
      path: ["type"]
    });
  }
  if (data.answerKey !== void 0 && data.payload !== void 0 && (data.payload.type !== "question" || data.payload.questionData.questionType !== "chat_agent_question")) {
    ctx.addIssue({
      code: "custom",
      message: "answerKey is only valid for a chat_agent_question payload",
      path: ["answerKey"]
    });
  }
});
var SaveItemRequestSchema = z.object({
  id: UnifiedItemSchema.shape.id.optional(),
  spaceId: UnifiedItemSchema.shape.spaceId,
  storyPointId: UnifiedItemSchema.shape.storyPointId,
  data: SaveItemDataSchema
}).strict();
var SaveItemResponseSchema = SaveOrDeleteResponseSchema;
var saveItemDef = defineCallable({
  name: "v1.levelup.saveItem",
  module: "levelup",
  requestSchema: SaveItemRequestSchema,
  responseSchema: SaveItemResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["items", "storyPoints", "versions"],
  authoritySensitive: true
});
var ImportFromBankRequestSchema = z.object({
  spaceId: z.string(),
  storyPointId: z.string(),
  bankItemIds: z.array(z.string()).min(1),
  targetType: zItemType.optional()
}).strict();
var ImportFromBankResponseSchema = z.object({ createdItemIds: z.array(z.string()) }).strict();
var importFromBankDef = defineCallable({
  name: "v1.levelup.importFromBank",
  module: "levelup",
  requestSchema: ImportFromBankRequestSchema,
  responseSchema: ImportFromBankResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  invalidates: ["items"]
});
var SaveAgentDataSchema = z.object({
  type: zAgentType,
  name: z.string().min(1),
  publicDescription: z.string().optional(),
  identity: z.string().optional(),
  isActive: z.boolean(),
  systemPrompt: z.string().optional(),
  supportedLanguages: z.array(z.string()).optional(),
  defaultLanguage: z.string().optional(),
  maxConversationTurns: z.number().int().optional(),
  rules: z.array(z.string()).optional(),
  openingMessage: z.string().optional(),
  evaluationObjectives: z.array(z.string()).optional(),
  strictness: z.number().optional(),
  feedbackStyle: z.string().optional(),
  modelPolicyId: zModelPolicyId,
  temperatureOverride: z.number().optional(),
  /** Legacy wording only: this is a CAS deactivation, never a hard delete. */
  deleted: z.boolean().optional()
}).strict().superRefine((data, ctx) => {
  if (data.deleted === true && data.isActive !== false) {
    ctx.addIssue({
      code: "custom",
      message: "deleted:true requires isActive:false; agents are deactivated, never hard-deleted",
      path: ["isActive"]
    });
  }
});
var SaveAgentCreateRequestSchema = z.object({
  id: z.undefined().optional(),
  expectedVersion: z.literal(0).optional(),
  spaceId: zSpaceId,
  data: SaveAgentDataSchema
}).strict().superRefine((request, ctx) => {
  if (request.data.deleted === true) {
    ctx.addIssue({
      code: "custom",
      message: "deleted:true requires an existing id and expectedVersion for CAS deactivation",
      path: ["data", "deleted"]
    });
  }
});
var SaveAgentUpdateRequestSchema = z.object({
  id: zAgentId,
  expectedVersion: z.number().int().min(1),
  spaceId: zSpaceId,
  data: SaveAgentDataSchema
}).strict();
var SaveAgentRequestSchema = z.union([
  SaveAgentCreateRequestSchema,
  SaveAgentUpdateRequestSchema
]);
var SaveAgentResponseSchema = z.object({
  id: zAgentId,
  created: z.boolean(),
  semanticChanged: z.boolean(),
  version: z.number().int().positive(),
  /** Present only for the retained deleted:true compatibility action. */
  deleted: z.literal(true).optional()
}).strict();
var saveAgentDef = defineCallable({
  name: "v1.levelup.saveAgent",
  module: "levelup",
  requestSchema: SaveAgentRequestSchema,
  responseSchema: SaveAgentResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["agents"],
  authoritySensitive: true
});
var SaveRubricPresetDataSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  rubric: UnifiedRubricSchema,
  category: zRubricPresetCategory,
  questionTypes: z.array(zQuestionType).optional(),
  isDefault: z.boolean().optional(),
  deleted: z.boolean().optional()
}).strict();
var SaveRubricPresetRequestSchema = z.object({
  id: z.string().optional(),
  data: SaveRubricPresetDataSchema
}).strict();
var SaveRubricPresetResponseSchema = SaveOrDeleteResponseSchema;
var saveRubricPresetDef = defineCallable({
  name: "v1.levelup.saveRubricPreset",
  module: "levelup",
  requestSchema: SaveRubricPresetRequestSchema,
  responseSchema: SaveRubricPresetResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["rubricPresets"],
  authoritySensitive: true
});
var SaveQuestionBankItemDataSchema = z.object({
  questionType: zQuestionType,
  title: z.string().optional(),
  content: z.string(),
  explanation: z.string().optional(),
  basePoints: z.number().optional(),
  questionData: QuestionTypeDataSchema,
  subject: z.string(),
  topics: z.array(z.string()),
  difficulty: zDifficulty,
  bloomsLevel: zBloomsLevel.optional(),
  tags: z.array(z.string()).optional(),
  deleted: z.boolean().optional()
}).strict();
var SaveQuestionBankItemRequestSchema = z.object({
  id: z.string().optional(),
  data: SaveQuestionBankItemDataSchema
}).strict();
var SaveQuestionBankItemResponseSchema = SaveOrDeleteResponseSchema;
var saveQuestionBankItemDef = defineCallable({
  name: "v1.levelup.saveQuestionBankItem",
  module: "levelup",
  requestSchema: SaveQuestionBankItemRequestSchema,
  responseSchema: SaveQuestionBankItemResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["questionBank"],
  authoritySensitive: true
});
var StartTestSessionRequestSchema = z.object({
  spaceId: z.string(),
  storyPointId: z.string()
}).strict();
var StartTestSessionResponseSchema = z.object({
  session: DigitalTestSessionViewSchema,
  resuming: z.boolean()
}).strict();
var startTestSessionDef = defineCallable({
  name: "v1.levelup.startTestSession",
  module: "levelup",
  requestSchema: StartTestSessionRequestSchema,
  responseSchema: StartTestSessionResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  invalidates: ["testSessions", "progress"],
  authoritySensitive: true
});
var SubmitTestSessionRequestSchema = z.object({
  sessionId: z.string(),
  autoSubmitted: z.boolean().optional()
}).strict();
var SubmitTestSessionResponseSchema = z.object({
  session: DigitalTestSessionResultViewSchema,
  progressUpdated: z.boolean()
}).strict();
var submitTestSessionDef = defineCallable({
  name: "v1.levelup.submitTestSession",
  module: "levelup",
  requestSchema: SubmitTestSessionRequestSchema,
  responseSchema: SubmitTestSessionResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  invalidates: ["testSessions", "progress", "storyPoints", "analytics"],
  authoritySensitive: true
});
var SaveTestAnswerRequestSchema = z.object({
  sessionId: z.string(),
  itemId: z.string(),
  answer: z.unknown(),
  markedForReview: z.boolean().optional(),
  timeSpentSeconds: z.number().int().optional()
}).strict();
var SaveTestAnswerResponseSchema = z.object({
  sessionId: z.string(),
  itemId: z.string(),
  saved: z.literal(true),
  answeredQuestions: z.number().int()
}).strict();
var saveTestAnswerDef = defineCallable({
  name: "v1.levelup.saveTestAnswer",
  module: "levelup",
  requestSchema: SaveTestAnswerRequestSchema,
  responseSchema: SaveTestAnswerResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  invalidates: ["testSessions"],
  authoritySensitive: true
});
var EvaluateAnswerRequestSchema = z.object({
  spaceId: z.string(),
  storyPointId: z.string().optional(),
  itemId: z.string(),
  answer: z.unknown(),
  mode: z.enum(["practice", "preview"]).optional(),
  // Captured image/audio the learner attached to this answer. These are
  // server-scoped Storage PATHS (e.g. `tenants/{tenantId}/exams/.../x.jpg`),
  // NOT web URLs — the same shape autograde's `imageUrls` carries — so the value
  // is a plain string, never `.url()` (which rejects bare paths). Tenant-scoping
  // is enforced server-side before the paths are attached to the AI grader.
  mediaUrls: z.array(z.string().min(1)).max(20).optional()
}).strict();
var EvaluateAnswerResponseSchema = z.object({
  evaluation: StoredEvaluationSchema,
  progressRecorded: z.boolean()
}).strict();
var evaluateAnswerDef = defineCallable({
  name: "v1.levelup.evaluateAnswer",
  module: "levelup",
  requestSchema: EvaluateAnswerRequestSchema,
  responseSchema: EvaluateAnswerResponseSchema,
  authMode: "authed",
  rateTier: "ai",
  idempotent: true,
  invalidates: ["storyPoints", "progress"],
  authoritySensitive: true
});
var RecordItemAttemptRequestSchema = z.object({
  spaceId: z.string(),
  storyPointId: z.string(),
  itemId: z.string(),
  answer: z.unknown(),
  timeSpent: z.number().optional()
}).strict();
var RecordItemAttemptResponseSchema = z.object({
  progress: ItemProgressViewSchema,
  completed: z.boolean()
}).strict();
var recordItemAttemptDef = defineCallable({
  name: "v1.levelup.recordItemAttempt",
  module: "levelup",
  requestSchema: RecordItemAttemptRequestSchema,
  responseSchema: RecordItemAttemptResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "domain:spaceId,storyPointId,itemId,answer",
  invalidates: ["progress"],
  // authoritySensitive:true — recordItemAttempt writes a GRADING OUTPUT (server
  // scores per CD13 / REVIEW §6.5). It is the ONE documented carve-out that is
  // ALSO on the OPTIMISTIC_ALLOWLIST: the optimistic patch shows an in-flight
  // attempt and reconciles from the authoritative {progress,completed} response
  // (A11) — the client never sends a score. The authority-flag-coverage test
  // requires this flag AND explicitly excepts recordItemAttempt from the
  // optimistic∩authority disjointness check (§3.1 / §4.4 / §6.5).
  authoritySensitive: true
});
var SendChatMessageRequestSchema = z.object({
  sessionId: z.string().optional(),
  spaceId: z.string(),
  storyPointId: z.string(),
  itemId: z.string(),
  text: z.string().min(1),
  mediaUrls: z.array(z.string().url()).optional(),
  language: z.string().optional()
}).strict();
var AgentObservationViewSchema = z.object({
  dimensionId: z.string(),
  evidence: z.string(),
  provisionalScore: z.number().optional()
}).strict();
var SendChatMessageResponseSchema = z.object({
  sessionId: z.string(),
  message: ChatMessageSchema,
  tokensUsed: z.number().int().optional(),
  // Chat-agent questions only (AI-EVALUATION-CORE-PLAN.md Phase 4):
  /** Rolling per-dimension scorecard accumulated so far (visible mid-conversation). */
  observations: z.array(AgentObservationViewSchema).optional(),
  /** True when this turn ended the conversation (agent tool or turn budget). */
  conversationEnded: z.boolean().optional(),
  /** Final evaluation over the transcript (present when grading succeeded). */
  evaluation: StoredEvaluationSchema.optional()
}).strict();
var sendChatMessageDef = defineCallable({
  name: "v1.levelup.sendChatMessage",
  module: "levelup",
  requestSchema: SendChatMessageRequestSchema,
  responseSchema: SendChatMessageResponseSchema,
  authMode: "authed",
  rateTier: "ai",
  invalidates: ["chat"]
});
var SaveSpaceReviewRequestSchema = z.object({
  spaceId: z.string(),
  rating: z.number().int().min(1).max(5),
  comment: z.string().optional()
}).strict();
var SaveSpaceReviewResponseSchema = z.object({
  success: z.boolean(),
  isUpdate: z.boolean()
}).strict();
var saveSpaceReviewDef = defineCallable({
  name: "v1.levelup.saveSpaceReview",
  module: "levelup",
  requestSchema: SaveSpaceReviewRequestSchema,
  responseSchema: SaveSpaceReviewResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["reviews", "spaces"]
});
var PurchaseSpaceRequestSchema = z.object({
  spaceId: z.string(),
  paymentToken: z.string().optional()
}).strict();
var PurchaseSpaceResponseSchema = z.object({
  success: z.boolean(),
  transactionId: z.string(),
  enrolledSpaceId: z.string()
}).strict();
var purchaseSpaceDef = defineCallable({
  name: "v1.levelup.purchaseSpace",
  module: "levelup",
  requestSchema: PurchaseSpaceRequestSchema,
  responseSchema: PurchaseSpaceResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  invalidates: ["store", "spaces", "enrollments"],
  authoritySensitive: true
});
var AssignContentWindowSchema = z.object({
  startAt: z.string().datetime().optional(),
  dueAt: z.string().datetime().optional()
}).strict();
var AssignContentRequestSchema = z.object({
  contentType: z.enum(["space", "exam"]),
  contentId: z.string(),
  classIds: z.array(z.string()).min(1),
  window: AssignContentWindowSchema.optional(),
  visibility: z.enum(["visible", "hidden", "scheduled"]).optional()
}).strict();
var AssignContentResponseSchema = SaveResponseSchema;
var assignContentDef = defineCallable({
  name: "v1.levelup.assignContent",
  module: "levelup",
  requestSchema: AssignContentRequestSchema,
  responseSchema: AssignContentResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  invalidates: ["assignment", "spaces", "exams"]
});
var GeneratedItemSchema = z.object({
  itemType: z.enum(["question", "material"]),
  questionType: zQuestionType.optional(),
  title: z.string(),
  payload: ItemPayloadSchema,
  bloomsLevel: z.string().optional(),
  topics: z.array(z.string()).optional(),
  suggestedRubric: UnifiedRubricSchema.optional()
}).strict();
var GenerateContentRequestSchema = z.object({
  storyPointId: z.string(),
  spaceId: z.string().optional(),
  spec: z.object({
    types: z.array(z.string()).min(1),
    count: z.number().int().min(1).max(50),
    difficulty: z.string().optional()
  }).strict(),
  sourcePdfPath: z.string().optional()
}).strict();
var GenerateContentResponseSchema = z.object({ drafts: z.array(GeneratedItemSchema) }).strict();
var generateContentDef = defineCallable({
  name: "v1.levelup.generateContent",
  module: "levelup",
  requestSchema: GenerateContentRequestSchema,
  responseSchema: GenerateContentResponseSchema,
  authMode: "authed",
  rateTier: "ai",
  authoritySensitive: true
});
var ConversationMediaInputSchema = z.object({
  mediaKind: z.literal("image"),
  storagePath: z.string().min(1),
  mimeType: z.string().min(1),
  altText: z.string().optional()
}).strict();
var QuestionHelpDraftSnapshotSchema = z.object({
  revision: z.number().int().nonnegative(),
  answer: zJsonValue
}).strict();

// src/callables/levelup/start-conversation.ts
var StartConversationRequestSchema = z.object({
  clientRequestId: z.string().uuid(),
  mode: zConversationMode,
  context: StartConversationContextSchema,
  locale: z.string().min(1).optional()
}).strict().superRefine((request, ctx) => {
  if (request.mode !== request.context.kind) {
    ctx.addIssue({
      code: "custom",
      message: "mode must match context.kind",
      path: ["mode"]
    });
  }
});
var StartConversationResponseSchema = z.object({
  session: ConversationSessionViewSchema,
  messages: z.array(ConversationMessageViewSchema),
  resumed: z.boolean()
}).strict();
var startConversationDef = defineCallable({
  name: "v1.levelup.startConversation",
  module: "levelup",
  requestSchema: StartConversationRequestSchema,
  responseSchema: StartConversationResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  invalidates: ["conversations"]
});
var SendConversationTurnInputSchema = z.object({
  text: z.string().min(1),
  media: z.array(ConversationMediaInputSchema).optional(),
  questionHelpDraft: QuestionHelpDraftSnapshotSchema.optional()
}).strict();
var SendConversationTurnRequestSchema = z.object({
  sessionId: zConversationSessionId,
  clientMessageId: z.string().uuid(),
  input: SendConversationTurnInputSchema
}).strict();
var SendConversationTurnResponseSchema = z.object({
  session: ConversationSessionViewSchema,
  acceptedMessage: ConversationMessageViewSchema,
  assistantMessages: z.array(ConversationMessageViewSchema),
  turn: ConversationTurnViewSchema,
  replayed: z.boolean()
}).strict();
var sendConversationTurnDef = defineCallable({
  name: "v1.levelup.sendConversationTurn",
  module: "levelup",
  requestSchema: SendConversationTurnRequestSchema,
  responseSchema: SendConversationTurnResponseSchema,
  authMode: "authed",
  rateTier: "ai",
  idempotent: true,
  idempotencyKey: "transport",
  invalidates: ["conversations"]
});
var FinishConversationRequestSchema = z.object({
  sessionId: zConversationSessionId,
  clientRequestId: z.string().uuid(),
  reason: z.literal("learner_requested"),
  earlyFinishConfirmed: z.boolean().optional()
}).strict();
var FinishConversationResultSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("completed"), evaluation: StoredEvaluationSchema.optional() }).strict(),
  z.object({ status: z.literal("grading_pending"), retryAfterMs: z.number().int().positive() }).strict(),
  z.object({
    status: z.literal("grading_failed"),
    retryable: z.boolean(),
    retryAfterMs: z.number().int().positive().optional()
  }).strict()
]);
var FinishConversationResponseSchema = z.object({
  session: ConversationSessionViewSchema,
  submission: ItemSubmissionViewSchema.optional(),
  result: FinishConversationResultSchema,
  replayed: z.boolean()
}).strict();
var finishConversationDef = defineCallable({
  name: "v1.levelup.finishConversation",
  module: "levelup",
  requestSchema: FinishConversationRequestSchema,
  responseSchema: FinishConversationResponseSchema,
  authMode: "authed",
  rateTier: "ai",
  idempotent: true,
  idempotencyKey: "transport",
  invalidates: ["conversations", "progress"],
  authoritySensitive: true
});
var AbandonConversationRequestSchema = z.object({
  sessionId: zConversationSessionId,
  clientRequestId: z.string().uuid()
}).strict();
var AbandonConversationResponseSchema = z.object({
  session: ConversationSessionViewSchema,
  replayed: z.boolean()
}).strict();
var abandonConversationDef = defineCallable({
  name: "v1.levelup.abandonConversation",
  module: "levelup",
  requestSchema: AbandonConversationRequestSchema,
  responseSchema: AbandonConversationResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  invalidates: ["conversations"],
  authoritySensitive: true
});
var SpaceFilterSchema = z.object({
  status: zSpaceStatus.optional(),
  type: zSpaceType.optional(),
  classId: z.string().optional(),
  subject: z.string().optional(),
  teacherId: z.string().optional()
}).strict();
var ListSpacesRequestSchema = withPaging(SpaceFilterSchema);
var ListSpacesResponseSchema = pageResponse(SpaceViewSchema);
var listSpacesDef = defineCallable({
  name: "v1.levelup.listSpaces",
  module: "levelup",
  requestSchema: ListSpacesRequestSchema,
  responseSchema: ListSpacesResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var GetSpaceRequestSchema = z.object({ spaceId: z.string() }).strict();
var GetSpaceResponseSchema = z.object({ space: SpaceViewSchema }).strict();
var getSpaceDef = defineCallable({
  name: "v1.levelup.getSpace",
  module: "levelup",
  requestSchema: GetSpaceRequestSchema,
  responseSchema: GetSpaceResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var zPageParamsShape = PageRequest.shape;
var SaveExamResponseSchema = zObject({
  id: zExamId,
  created: z.boolean()
});
var SaveEvaluationSettingsResponseSchema = zObject({
  id: zEvaluationSettingsId,
  created: z.boolean()
});
var SaveExamQuestionResponseSchema = zObject({
  id: zExamQuestionId,
  created: z.boolean(),
  deleted: z.boolean().optional()
});
var ExamListViewSchema = zObject({
  id: zExamId,
  title: z.string(),
  subject: z.string(),
  topics: z.array(z.string()),
  classIds: z.array(zClassId),
  examDate: zTimestamp,
  duration: z.number().int(),
  totalMarks: z.number(),
  passingMarks: z.number(),
  status: zExamStatus,
  academicSessionId: zAcademicSessionId.optional(),
  linkedSpaceId: zSpaceId.optional(),
  linkedSpaceTitle: z.string().optional(),
  stats: ExamStatsSchema.optional(),
  createdAt: zTimestamp,
  updatedAt: zTimestamp
});
var ExamDetailViewSchema = zObject({
  id: zExamId,
  title: z.string(),
  subject: z.string(),
  topics: z.array(z.string()),
  classIds: z.array(zClassId),
  sectionIds: z.array(zSectionId).optional(),
  examDate: zTimestamp,
  duration: z.number().int(),
  academicSessionId: zAcademicSessionId.optional(),
  totalMarks: z.number(),
  passingMarks: z.number(),
  status: zExamStatus,
  questionPaper: ExamQuestionPaperSchema.optional(),
  gradingConfig: ExamGradingConfigSchema,
  evaluationSettingsId: zEvaluationSettingsId.optional(),
  linkedSpaceId: zSpaceId.optional(),
  linkedSpaceTitle: z.string().optional(),
  linkedStoryPointId: zStoryPointId.optional(),
  stats: ExamStatsSchema.optional(),
  createdAt: zTimestamp,
  updatedAt: zTimestamp
});
var ExamQuestionViewSchema = zObject({
  id: zExamQuestionId,
  examId: zExamId,
  text: z.string(),
  imageUrls: z.array(z.string()).optional(),
  maxMarks: z.number(),
  order: z.number().int(),
  rubric: UnifiedRubricSchema,
  questionType: z.string().optional(),
  subQuestions: z.array(SubQuestionSchema).optional(),
  extractionConfidence: z.number().optional(),
  readabilityIssue: z.boolean().optional(),
  // Live extraction pipeline: "pending" while Pass-2 rubric generation is in
  // flight for this question, "generated" once its rubric lands.
  rubricStatus: z.enum(["pending", "generated"]).optional(),
  createdAt: zTimestamp.optional(),
  updatedAt: zTimestamp
});
var ExtractedQuestionSchema = zObject({
  id: zExamQuestionId.optional(),
  text: z.string(),
  maxMarks: z.number(),
  order: z.number().int(),
  rubric: UnifiedRubricSchema.optional(),
  questionType: z.string().optional(),
  subQuestions: z.array(SubQuestionSchema).optional(),
  extractionConfidence: z.number().optional(),
  readabilityIssue: z.boolean().optional(),
  rubricStatus: z.enum(["pending", "generated"]).optional(),
  // ⚷ server-only rubric guidance — present in the AI extraction response and
  // persisted to the question doc; stripped from student/scanner projections.
  modelAnswer: z.string().optional(),
  evaluationGuidance: z.string().optional()
});
var SubmissionListViewSchema = zObject({
  id: zSubmissionId,
  examId: zExamId,
  studentId: zStudentId,
  studentName: z.string(),
  rollNumber: z.string(),
  classId: zClassId,
  pipelineStatus: zSubmissionPipelineStatus,
  summary: SubmissionSummarySchema,
  gradingProgress: GradingProgressSchema.optional(),
  resultsReleased: z.boolean(),
  uploadedBy: z.string().optional(),
  createdAt: zTimestamp,
  updatedAt: zTimestamp
});
var SubmissionDetailViewSchema = zObject({
  id: zSubmissionId,
  examId: zExamId,
  studentId: zStudentId,
  studentName: z.string(),
  rollNumber: z.string(),
  classId: zClassId,
  answerSheets: AnswerSheetDataSchema,
  scoutingResult: ScoutingResultSchema.optional(),
  // ⚷ release-gated: the score summary is WITHHELD (omitted) for an owner reading
  // BEFORE results are released (§6.10 stripped projection), so it is optional here.
  summary: SubmissionSummarySchema.optional(),
  pipelineStatus: zSubmissionPipelineStatus,
  pipelineError: z.string().optional(),
  retryCount: z.number().int(),
  gradingProgress: GradingProgressSchema.optional(),
  resultsReleased: z.boolean(),
  resultsReleasedAt: zTimestamp.nullable(),
  createdAt: zTimestamp,
  updatedAt: zTimestamp
});
var QuestionSubmissionViewSchema = zObject({
  id: zQuestionSubmissionId,
  submissionId: zSubmissionId,
  questionId: zExamQuestionId,
  examId: zExamId,
  mapping: QuestionMappingSchema,
  evaluation: UnifiedEvaluationResultSchema.optional(),
  gradingStatus: zQuestionGradingStatus,
  gradingError: z.string().optional(),
  manualOverride: ManualOverrideSchema.optional(),
  createdAt: zTimestamp,
  updatedAt: zTimestamp
});
var EvaluationSettingsViewSchema = zObject({
  id: zEvaluationSettingsId,
  name: z.string(),
  description: z.string().optional(),
  isDefault: z.boolean(),
  isPublic: z.boolean().optional(),
  enabledDimensions: z.array(EvaluationDimensionSchema),
  displaySettings: EvaluationDisplaySettingsSchema,
  confidenceConfig: EvaluationConfidenceConfigSchema.optional(),
  usageQuota: UsageQuotaConfigSchema.optional(),
  createdAt: zTimestamp,
  updatedAt: zTimestamp
});
var DeadLetterViewSchema = zObject({
  id: zDeadLetterEntryId,
  submissionId: zSubmissionId,
  questionSubmissionId: zQuestionSubmissionId.optional(),
  pipelineStep: z.enum(GRADING_PIPELINE_STEPS),
  error: z.string(),
  attempts: z.number().int(),
  lastAttemptAt: zTimestamp,
  resolvedAt: zTimestamp.nullable(),
  resolutionMethod: z.enum(DEAD_LETTER_RESOLUTION_METHODS).optional(),
  createdAt: zTimestamp
});
var ExamAnalyticsViewSchema = zObject({
  examId: zExamId,
  totalSubmissions: z.number().int(),
  gradedSubmissions: z.number().int(),
  avgScore: z.number(),
  avgPercentage: z.number(),
  passRate: z.number(),
  medianScore: z.number(),
  scoreDistribution: ScoreDistributionSchema,
  questionAnalytics: z.record(z.string(), QuestionAnalyticsEntrySchema),
  classBreakdown: z.record(z.string(), ClassBreakdownEntrySchema),
  topicPerformance: z.record(z.string(), TopicPerformanceEntrySchema),
  computedAt: zTimestamp,
  lastUpdatedAt: zTimestamp
});

// src/callables/levelup/get-evaluation-config.ts
var EvaluationConfigSourceSchema = z.enum([
  "item",
  "space",
  "exam",
  "tenant_default",
  "none"
]);
var EvaluationConfigProvenanceSchema = z.object({
  agentSource: EvaluationConfigSourceSchema,
  rubricSource: EvaluationConfigSourceSchema,
  settingsSource: EvaluationConfigSourceSchema
}).strict();
var EvaluationConfigViewSchema = z.object({
  agent: AgentViewSchema.nullable(),
  rubric: UnifiedRubricSchema.nullable(),
  settings: EvaluationSettingsViewSchema.nullable(),
  provenance: EvaluationConfigProvenanceSchema
}).strict();
var GetEvaluationConfigRequestSchema = z.object({
  spaceId: z.string(),
  /** Omit to preview the space-level defaults (space settings screen). */
  itemId: z.string().optional()
}).strict();
var GetEvaluationConfigResponseSchema = z.object({ config: EvaluationConfigViewSchema }).strict();
var getEvaluationConfigDef = defineCallable({
  name: "v1.levelup.getEvaluationConfig",
  module: "levelup",
  requestSchema: GetEvaluationConfigRequestSchema,
  responseSchema: GetEvaluationConfigResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListStoryPointsRequestSchema = z.object({ spaceId: z.string() }).strict();
var ListStoryPointsResponseSchema = z.object({ items: z.array(StoryPointViewSchema) }).strict();
var listStoryPointsDef = defineCallable({
  name: "v1.levelup.listStoryPoints",
  module: "levelup",
  requestSchema: ListStoryPointsRequestSchema,
  responseSchema: ListStoryPointsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var GetStoryPointRequestSchema = z.object({ spaceId: z.string(), storyPointId: z.string() }).strict();
var GetStoryPointResponseSchema = z.object({ storyPoint: StoryPointViewSchema }).strict();
var getStoryPointDef = defineCallable({
  name: "v1.levelup.getStoryPoint",
  module: "levelup",
  requestSchema: GetStoryPointRequestSchema,
  responseSchema: GetStoryPointResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListItemsRequestSchema = withPaging(
  z.object({ spaceId: z.string(), storyPointId: z.string() }).strict()
);
var ListItemsResponseSchema = pageResponse(ItemViewSchema);
var listItemsDef = defineCallable({
  name: "v1.levelup.listItems",
  module: "levelup",
  requestSchema: ListItemsRequestSchema,
  responseSchema: ListItemsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var GetItemForEditRequestSchema = z.object({ spaceId: z.string(), storyPointId: z.string(), itemId: z.string() }).strict();
var GetItemForEditResponseSchema = z.object({ item: ItemEditViewSchema }).strict();
var getItemForEditDef = defineCallable({
  name: "v1.levelup.getItemForEdit",
  module: "levelup",
  requestSchema: GetItemForEditRequestSchema,
  responseSchema: GetItemForEditResponseSchema,
  authMode: "authed",
  rateTier: "read",
  authoritySensitive: true
});
var ListVersionsRequestSchema = withPaging(z.object({ spaceId: z.string() }).strict());
var ListVersionsResponseSchema = pageResponse(ContentVersionSchema);
var listVersionsDef = defineCallable({
  name: "v1.levelup.listVersions",
  module: "levelup",
  requestSchema: ListVersionsRequestSchema,
  responseSchema: ListVersionsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var QuestionBankFilterSchema = z.object({
  questionType: zQuestionType.optional(),
  subject: z.string().optional(),
  difficulty: zDifficulty.optional(),
  bloomsLevel: zBloomsLevel.optional(),
  topic: z.string().optional(),
  search: z.string().optional()
}).strict();
var ListQuestionBankRequestSchema = withPaging(QuestionBankFilterSchema);
var ListQuestionBankResponseSchema = pageResponse(QuestionBankItemSchema);
var listQuestionBankDef = defineCallable({
  name: "v1.levelup.listQuestionBank",
  module: "levelup",
  requestSchema: ListQuestionBankRequestSchema,
  responseSchema: ListQuestionBankResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListRubricPresetsRequestSchema = z.object({
  category: zRubricPresetCategory.optional(),
  questionType: zQuestionType.optional()
}).strict();
var ListRubricPresetsResponseSchema = z.object({ items: z.array(RubricPresetSchema) }).strict();
var listRubricPresetsDef = defineCallable({
  name: "v1.levelup.listRubricPresets",
  module: "levelup",
  requestSchema: ListRubricPresetsRequestSchema,
  responseSchema: ListRubricPresetsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListAgentsRequestSchema = z.object({ spaceId: z.string() }).strict();
var ListAgentsResponseSchema = z.object({ items: z.array(AgentViewSchema) }).strict();
var listAgentsDef = defineCallable({
  name: "v1.levelup.listAgents",
  module: "levelup",
  requestSchema: ListAgentsRequestSchema,
  responseSchema: ListAgentsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListChatSessionsRequestSchema = withPaging(
  z.object({ spaceId: z.string().optional(), itemId: z.string().optional() }).strict()
);
var ListChatSessionsResponseSchema = pageResponse(ChatSessionSummarySchema);
var listChatSessionsDef = defineCallable({
  name: "v1.levelup.listChatSessions",
  module: "levelup",
  requestSchema: ListChatSessionsRequestSchema,
  responseSchema: ListChatSessionsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var GetChatSessionRequestSchema = z.object({ sessionId: z.string() }).strict();
var GetChatSessionResponseSchema = z.object({ session: ChatSessionViewSchema }).strict();
var getChatSessionDef = defineCallable({
  name: "v1.levelup.getChatSession",
  module: "levelup",
  requestSchema: GetChatSessionRequestSchema,
  responseSchema: GetChatSessionResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListSpaceReviewsRequestSchema = withPaging(z.object({ spaceId: z.string() }).strict());
var ListSpaceReviewsResponseSchema = pageResponse(SpaceReviewSchema);
var listSpaceReviewsDef = defineCallable({
  name: "v1.levelup.listSpaceReviews",
  module: "levelup",
  requestSchema: ListSpaceReviewsRequestSchema,
  responseSchema: ListSpaceReviewsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListStoreSpacesRequestSchema = withPaging(
  z.object({ subject: z.string().optional(), search: z.string().optional() }).strict()
);
var ListStoreSpacesResponseSchema = pageResponse(StoreSpaceListingSchema);
var listStoreSpacesDef = defineCallable({
  name: "v1.levelup.listStoreSpaces",
  module: "levelup",
  requestSchema: ListStoreSpacesRequestSchema,
  responseSchema: ListStoreSpacesResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var GetStoreSpaceRequestSchema = z.object({ spaceId: z.string() }).strict();
var GetStoreSpaceResponseSchema = z.object({ listing: StoreSpaceListingSchema }).strict();
var getStoreSpaceDef = defineCallable({
  name: "v1.levelup.getStoreSpace",
  module: "levelup",
  requestSchema: GetStoreSpaceRequestSchema,
  responseSchema: GetStoreSpaceResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var GetSpaceProgressRequestSchema = z.object({ spaceId: z.string(), userId: z.string().optional() }).strict();
var GetSpaceProgressResponseSchema = z.object({ progress: SpaceProgressViewSchema.nullable() }).strict();
var getSpaceProgressDef = defineCallable({
  name: "v1.levelup.getSpaceProgress",
  module: "levelup",
  requestSchema: GetSpaceProgressRequestSchema,
  responseSchema: GetSpaceProgressResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var GetStoryPointProgressRequestSchema = z.object({ spaceId: z.string(), storyPointId: z.string(), userId: z.string().optional() }).strict();
var GetStoryPointProgressResponseSchema = z.object({ progress: StoryPointProgressDocViewSchema.nullable() }).strict();
var getStoryPointProgressDef = defineCallable({
  name: "v1.levelup.getStoryPointProgress",
  module: "levelup",
  requestSchema: GetStoryPointProgressRequestSchema,
  responseSchema: GetStoryPointProgressResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListSpaceProgressForUserRequestSchema = withPaging(
  z.object({ userId: z.string() }).strict()
);
var ListSpaceProgressForUserResponseSchema = pageResponse(SpaceProgressViewSchema);
var listSpaceProgressForUserDef = defineCallable({
  name: "v1.levelup.listSpaceProgressForUser",
  module: "levelup",
  requestSchema: ListSpaceProgressForUserRequestSchema,
  responseSchema: ListSpaceProgressForUserResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var GetTestSessionRequestSchema = z.object({ sessionId: z.string() }).strict();
var GetTestSessionResponseSchema = z.object({ session: DigitalTestSessionViewSchema }).strict();
var getTestSessionDef = defineCallable({
  name: "v1.levelup.getTestSession",
  module: "levelup",
  requestSchema: GetTestSessionRequestSchema,
  responseSchema: GetTestSessionResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListTestSessionsRequestSchema = withPaging(
  z.object({
    spaceId: z.string().optional(),
    storyPointId: z.string().optional(),
    userId: z.string().optional(),
    status: zTestSessionStatus.optional(),
    latestOnly: z.boolean().optional()
  }).strict()
);
var ListTestSessionsResponseSchema = pageResponse(DigitalTestSessionSummaryViewSchema);
var listTestSessionsDef = defineCallable({
  name: "v1.levelup.listTestSessions",
  module: "levelup",
  requestSchema: ListTestSessionsRequestSchema,
  responseSchema: ListTestSessionsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var GetConversationRequestSchema = z.object({
  sessionId: zConversationSessionId,
  messageCursor: z.string().min(1).optional(),
  messageLimit: z.number().int().min(1).max(100).optional()
}).strict();
var GetConversationResponseSchema = z.object({
  session: ConversationSessionViewSchema,
  messages: z.array(ConversationMessageViewSchema),
  nextMessageCursor: z.string().nullable(),
  activeTurn: ConversationTurnViewSchema.optional()
}).strict();
var getConversationDef = defineCallable({
  name: "v1.levelup.getConversation",
  module: "levelup",
  requestSchema: GetConversationRequestSchema,
  responseSchema: GetConversationResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListConversationsRequestSchema = z.object({
  mode: zConversationMode.optional(),
  status: zConversationSessionStatus.optional(),
  context: StartConversationContextSchema.optional(),
  cursor: z.string().min(1).optional(),
  limit: z.number().int().min(1).max(50).optional()
}).strict().superRefine((request, ctx) => {
  if (request.mode !== void 0 && request.context !== void 0 && request.mode !== request.context.kind) {
    ctx.addIssue({
      code: "custom",
      message: "mode must match context.kind when both are supplied",
      path: ["mode"]
    });
  }
});
var ListConversationsResponseSchema = z.object({
  items: z.array(ConversationSessionSummaryViewSchema),
  nextCursor: z.string().nullable()
}).strict();
var listConversationsDef = defineCallable({
  name: "v1.levelup.listConversations",
  module: "levelup",
  requestSchema: ListConversationsRequestSchema,
  responseSchema: ListConversationsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});

// src/callables/levelup/index.ts
var LEVELUP_CONTENT_CALLABLES = {
  "v1.levelup.saveSpace": saveSpaceDef,
  "v1.levelup.duplicateSpace": duplicateSpaceDef,
  "v1.levelup.saveStoryPoint": saveStoryPointDef,
  "v1.levelup.saveItem": saveItemDef,
  "v1.levelup.importFromBank": importFromBankDef,
  "v1.levelup.saveAgent": saveAgentDef,
  "v1.levelup.saveRubricPreset": saveRubricPresetDef,
  "v1.levelup.saveQuestionBankItem": saveQuestionBankItemDef,
  "v1.levelup.startTestSession": startTestSessionDef,
  "v1.levelup.submitTestSession": submitTestSessionDef,
  "v1.levelup.saveTestAnswer": saveTestAnswerDef,
  "v1.levelup.evaluateAnswer": evaluateAnswerDef,
  "v1.levelup.recordItemAttempt": recordItemAttemptDef,
  "v1.levelup.sendChatMessage": sendChatMessageDef,
  "v1.levelup.saveSpaceReview": saveSpaceReviewDef,
  "v1.levelup.purchaseSpace": purchaseSpaceDef,
  "v1.levelup.assignContent": assignContentDef,
  "v1.levelup.generateContent": generateContentDef,
  "v1.levelup.startConversation": startConversationDef,
  "v1.levelup.sendConversationTurn": sendConversationTurnDef,
  "v1.levelup.finishConversation": finishConversationDef,
  "v1.levelup.abandonConversation": abandonConversationDef,
  "v1.levelup.listSpaces": listSpacesDef,
  "v1.levelup.getSpace": getSpaceDef,
  "v1.levelup.getEvaluationConfig": getEvaluationConfigDef,
  "v1.levelup.listStoryPoints": listStoryPointsDef,
  "v1.levelup.getStoryPoint": getStoryPointDef,
  "v1.levelup.listItems": listItemsDef,
  "v1.levelup.getItemForEdit": getItemForEditDef,
  "v1.levelup.listVersions": listVersionsDef,
  "v1.levelup.listQuestionBank": listQuestionBankDef,
  "v1.levelup.listRubricPresets": listRubricPresetsDef,
  "v1.levelup.listAgents": listAgentsDef,
  "v1.levelup.listChatSessions": listChatSessionsDef,
  "v1.levelup.getChatSession": getChatSessionDef,
  "v1.levelup.listSpaceReviews": listSpaceReviewsDef,
  "v1.levelup.listStoreSpaces": listStoreSpacesDef,
  "v1.levelup.getStoreSpace": getStoreSpaceDef,
  "v1.levelup.getSpaceProgress": getSpaceProgressDef,
  "v1.levelup.getStoryPointProgress": getStoryPointProgressDef,
  "v1.levelup.listSpaceProgressForUser": listSpaceProgressForUserDef,
  "v1.levelup.getTestSession": getTestSessionDef,
  "v1.levelup.listTestSessions": listTestSessionsDef,
  "v1.levelup.getConversation": getConversationDef,
  "v1.levelup.listConversations": listConversationsDef
};
Object.values(LEVELUP_CONTENT_CALLABLES).map(
  (d) => d.name
);
var GetGamificationSummaryRequestSchema = z.object({ userId: z.string().optional() }).strict();
var getGamificationSummaryDef = defineCallable({
  name: "v1.levelup.getGamificationSummary",
  module: "levelup",
  requestSchema: GetGamificationSummaryRequestSchema,
  responseSchema: GamificationSummarySchema,
  authMode: "authed",
  rateTier: "read"
});
var GetStudentLevelRequestSchema = z.object({ userId: z.string().optional() }).strict();
var getStudentLevelDef = defineCallable({
  name: "v1.levelup.getStudentLevel",
  module: "levelup",
  requestSchema: GetStudentLevelRequestSchema,
  responseSchema: StudentLevelSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListAchievementsRequestSchema = z.object({ category: z.string().optional(), onlyActive: z.boolean().optional() }).extend(PageRequest.shape).strict();
var AchievementWithEarnedStateSchema = AchievementSchema.extend({
  earned: z.boolean()
}).strict();
var ListAchievementsResponseSchema = pageResponse(AchievementWithEarnedStateSchema);
var listAchievementsDef = defineCallable({
  name: "v1.levelup.listAchievements",
  module: "levelup",
  requestSchema: ListAchievementsRequestSchema,
  responseSchema: ListAchievementsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListStudentAchievementsRequestSchema = z.object({ userId: z.string().optional(), unseenOnly: z.boolean().optional() }).extend(PageRequest.shape).strict();
var ListStudentAchievementsResponseSchema = pageResponse(StudentAchievementSchema);
var listStudentAchievementsDef = defineCallable({
  name: "v1.levelup.listStudentAchievements",
  module: "levelup",
  requestSchema: ListStudentAchievementsRequestSchema,
  responseSchema: ListStudentAchievementsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var MarkAchievementsSeenRequestSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("ids"), achievementIds: z.array(z.string()) }).strict(),
  z.object({ mode: z.literal("all") }).strict()
]);
var MarkAchievementsSeenResponseSchema = z.object({ updated: z.number().int().nonnegative() }).strict();
var markAchievementsSeenDef = defineCallable({
  name: "v1.levelup.markAchievementsSeen",
  module: "levelup",
  requestSchema: MarkAchievementsSeenRequestSchema,
  responseSchema: MarkAchievementsSeenResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  invalidates: ["achievements", "gamification"]
});
var SaveAchievementDefinitionRequestSchema = z.object({
  id: z.string().optional(),
  data: z.record(z.string(), z.unknown()),
  delete: z.boolean().optional()
}).strict();
var saveAchievementDefinitionDef = defineCallable({
  name: "v1.levelup.saveAchievementDefinition",
  module: "levelup",
  requestSchema: SaveAchievementDefinitionRequestSchema,
  responseSchema: SaveResponseSchema,
  authMode: "authed",
  rateTier: "write",
  invalidates: ["achievements"],
  authoritySensitive: true
});
var GetLeaderboardRequestSchema = z.object({
  scope: z.string(),
  spaceId: z.string().optional(),
  storyPointId: z.string().optional()
}).extend(PageRequest.shape).strict();
var GetLeaderboardResponseSchema = pageResponse(LeaderboardEntrySchema).extend({ callerEntry: LeaderboardEntrySchema.nullable() }).strict();
var getLeaderboardDef = defineCallable({
  name: "v1.levelup.getLeaderboard",
  module: "levelup",
  requestSchema: GetLeaderboardRequestSchema,
  responseSchema: GetLeaderboardResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListStudyGoalsRequestSchema = z.object({ userId: z.string().optional(), includeCompleted: z.boolean().optional() }).extend(PageRequest.shape).strict();
var ListStudyGoalsResponseSchema = pageResponse(StudyGoalSchema);
var listStudyGoalsDef = defineCallable({
  name: "v1.levelup.listStudyGoals",
  module: "levelup",
  requestSchema: ListStudyGoalsRequestSchema,
  responseSchema: ListStudyGoalsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var SaveStudyGoalRequestSchema = z.object({ id: z.string().optional(), data: z.record(z.string(), z.unknown()) }).strict();
var saveStudyGoalDef = defineCallable({
  name: "v1.levelup.saveStudyGoal",
  module: "levelup",
  requestSchema: SaveStudyGoalRequestSchema,
  responseSchema: SaveResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  invalidates: ["studyGoals"]
});
var ListStudySessionsRequestSchema = z.object({
  userId: z.string().optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional()
}).strict();
var ListStudySessionsResponseSchema = z.object({
  sessions: z.array(StudySessionSchema),
  streakDays: z.number().int().nonnegative(),
  longestStreak: z.number().int().nonnegative()
}).strict();
var listStudySessionsDef = defineCallable({
  name: "v1.levelup.listStudySessions",
  module: "levelup",
  requestSchema: ListStudySessionsRequestSchema,
  responseSchema: ListStudySessionsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListLearningInsightsRequestSchema = z.object({ studentId: z.string().optional(), type: z.string().optional() }).extend(PageRequest.shape).strict();
var ListLearningInsightsResponseSchema = pageResponse(LearningInsightSchema);
var listLearningInsightsDef = defineCallable({
  name: "v1.levelup.listLearningInsights",
  module: "levelup",
  requestSchema: ListLearningInsightsRequestSchema,
  responseSchema: ListLearningInsightsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var DismissInsightRequestSchema = z.object({ insightId: z.string() }).strict();
var DismissInsightResponseSchema = z.object({ id: z.string(), dismissed: z.literal(true) }).strict();
var dismissInsightDef = defineCallable({
  name: "v1.levelup.dismissInsight",
  module: "levelup",
  requestSchema: DismissInsightRequestSchema,
  responseSchema: DismissInsightResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  invalidates: ["insights"]
});
var GAMIFICATION_CALLABLES = {
  "v1.levelup.getGamificationSummary": getGamificationSummaryDef,
  "v1.levelup.getStudentLevel": getStudentLevelDef,
  "v1.levelup.listAchievements": listAchievementsDef,
  "v1.levelup.listStudentAchievements": listStudentAchievementsDef,
  "v1.levelup.markAchievementsSeen": markAchievementsSeenDef,
  "v1.levelup.saveAchievementDefinition": saveAchievementDefinitionDef,
  "v1.levelup.getLeaderboard": getLeaderboardDef,
  "v1.levelup.listStudyGoals": listStudyGoalsDef,
  "v1.levelup.saveStudyGoal": saveStudyGoalDef,
  "v1.levelup.listStudySessions": listStudySessionsDef,
  "v1.levelup.listLearningInsights": listLearningInsightsDef,
  "v1.levelup.dismissInsight": dismissInsightDef
};
var SaveExamDataSchema = zObject({
  title: z.string().optional(),
  subject: z.string().optional(),
  topics: z.array(z.string()).optional(),
  classIds: z.array(zClassId).optional(),
  sectionIds: z.array(zSectionId).optional(),
  examDate: zTimestamp.optional(),
  duration: z.number().int().optional(),
  academicSessionId: zAcademicSessionId.optional(),
  totalMarks: z.number().optional(),
  passingMarks: z.number().optional(),
  gradingConfig: ExamGradingConfigSchema.optional(),
  linkedSpaceId: zSpaceId.optional(),
  linkedSpaceTitle: z.string().optional(),
  linkedStoryPointId: zStoryPointId.optional(),
  status: zExamStatus.optional(),
  evaluationSettingsId: zEvaluationSettingsId.optional(),
  questionPaperImages: z.array(z.string()).optional()
});
var SaveExamRequestSchema = zObject({
  id: zExamId.optional(),
  data: SaveExamDataSchema
});
var saveExamDef = {
  name: "v1.autograde.saveExam",
  module: "autograde",
  requestSchema: SaveExamRequestSchema,
  responseSchema: SaveExamResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  // ⚷ exam lifecycle (publish + post-publish-locked fields) is server-authoritative.
  authoritySensitive: true,
  invalidates: ["exams"]
};
var SaveExamQuestionDataSchema = zObject({
  text: z.string().optional(),
  maxMarks: z.number().optional(),
  order: z.number().int().optional(),
  questionType: zQuestionType.optional(),
  rubric: UnifiedRubricSchema.optional(),
  subQuestions: z.array(SubQuestionSchema).optional(),
  /** Storage paths under `tenants/{tenantId}/` — validated server-side. */
  imageUrls: z.array(z.string()).optional()
});
var SaveExamQuestionRequestSchema = zObject({
  /** Omit to create (server assigns deterministic `{examId}_q{order}`). */
  id: zExamQuestionId.optional(),
  examId: zExamId,
  /** Required for create/update; omitted for delete. */
  data: SaveExamQuestionDataSchema.optional(),
  /** Set to `true` to delete the question (requires `id`). */
  delete: z.literal(true).optional()
});
var saveExamQuestionDef = {
  name: "v1.autograde.saveExamQuestion",
  module: "autograde",
  requestSchema: SaveExamQuestionRequestSchema,
  responseSchema: SaveExamQuestionResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  // ⚷ structural question edits / delete affect exam publish-readiness.
  authoritySensitive: true,
  invalidates: ["questions"]
};
var EXTRACT_QUESTIONS_MODES = ["full", "single", "rubrics"];
var ExtractQuestionsRequestSchema = zObject({
  examId: zExamId,
  mode: z.enum(EXTRACT_QUESTIONS_MODES).optional(),
  questionNumber: z.string().optional()
});
var ExtractQuestionsMetadataSchema = zObject({
  questionCount: z.number().int(),
  tokensUsed: z.number(),
  cost: z.number(),
  extractedAt: z.string(),
  imageQualityAcceptable: z.boolean(),
  mode: z.enum(EXTRACT_QUESTIONS_MODES).optional()
});
var ExtractQuestionsResponseSchema = zObject({
  success: z.boolean(),
  questions: z.array(ExtractedQuestionSchema),
  warnings: z.array(z.string()),
  metadata: ExtractQuestionsMetadataSchema
});
var extractQuestionsDef = {
  name: "v1.autograde.extractQuestions",
  module: "autograde",
  requestSchema: ExtractQuestionsRequestSchema,
  responseSchema: ExtractQuestionsResponseSchema,
  authMode: "authed",
  rateTier: "ai",
  idempotent: true,
  idempotencyKey: "transport",
  // ⚷ AI call + cost rollup + rubric-snapshot write are server-authoritative.
  authoritySensitive: true,
  invalidates: ["exams"]
};
var UploadAnswerSheetsRequestSchema = zObject({
  examId: zExamId,
  studentId: zStudentId,
  classId: zClassId,
  imageUrls: z.array(z.string()).min(1).max(50),
  // ⚷ Explicit REPLACE confirmation (owner directive 2026-07-19). When a submission
  // already exists for (examId, studentId), the server REJECTS the upload
  // (FAILED_PRECONDITION, `meta.reason='submission_exists'`) UNLESS `replace===true`.
  // A `replace` re-points the existing submission at the new answer sheets, resets its
  // grading/release state, and re-runs the pipeline — so released results are never
  // silently destroyed by a re-upload. Optional & absent-by-default → a scanner /
  // first-time upload is unaffected. See uploadAnswerSheetsService.
  replace: z.boolean().optional()
});
var UploadAnswerSheetsResponseSchema = zObject({
  submissionId: zSubmissionId,
  // Outcome flag so the client can message honestly (created vs replaced). Optional
  // for wire back-compat with a pre-replace backend (older deploy omits it → treated
  // as a plain create).
  replaced: z.boolean().optional()
});
var uploadAnswerSheetsDef = {
  name: "v1.autograde.uploadAnswerSheets",
  module: "autograde",
  requestSchema: UploadAnswerSheetsRequestSchema,
  responseSchema: UploadAnswerSheetsResponseSchema,
  authMode: "authed",
  rateTier: "ai",
  idempotent: true,
  // Domain dedupe on (uid, examId, studentId, imageUrls-hash): a scanner network
  // retry (identical paths) is deduped, but a genuine RE-upload (new scan paths) runs
  // fresh — the one-submission-per-student invariant + released-result protection is
  // enforced in the service body, not by a permanent business key (which silently
  // swallowed re-uploads, owner directive 2026-07-19).
  idempotencyKey: "domain:examId+studentId+contentHash",
  // ⚷ creates the Submission + advances exam→grading + counters (server authority).
  authoritySensitive: true,
  invalidates: ["submissions", "exams"]
};
var ManualBranchSchema = zObject({
  mode: z.literal("manual"),
  submissionId: zSubmissionId,
  questionId: zExamQuestionId,
  score: z.number(),
  feedback: z.string().optional()
});
var RetryBranchSchema = zObject({
  mode: z.literal("retry"),
  submissionId: zSubmissionId,
  examId: zExamId.optional(),
  questionIds: z.array(zExamQuestionId).optional()
});
var AiBranchSchema = zObject({
  mode: z.literal("ai"),
  submissionId: zSubmissionId,
  questionId: zExamQuestionId
});
var GradeQuestionRequestSchema = z.discriminatedUnion("mode", [
  ManualBranchSchema,
  RetryBranchSchema,
  AiBranchSchema
]);
var GradeQuestionResponseSchema = zObject({
  success: z.boolean(),
  updatedScore: z.number().optional(),
  gradingStatus: zQuestionGradingStatus.optional(),
  retriedCount: z.number().int().optional()
});
var gradeQuestionDef = {
  name: "v1.autograde.gradeQuestion",
  module: "autograde",
  requestSchema: GradeQuestionRequestSchema,
  responseSchema: GradeQuestionResponseSchema,
  authMode: "authed",
  rateTier: "ai",
  // NOT idempotent (api-client-core §3.5 inventory): gradeQuestion (manual/retry/ai)
  // is a non-idempotent write — no UUIDv7 key on the wire, bypasses the offline
  // queue, and is never auto-retried.
  // ⚷ score authority — the client never writes its own score.
  authoritySensitive: true,
  invalidates: ["submissions"]
};
var ReleaseResultsRequestSchema = zObject({
  examId: zExamId,
  classIds: z.array(zClassId).optional()
});
var ReleaseResultsResponseSchema = zObject({
  id: zExamId,
  releasedCount: z.number().int(),
  created: z.literal(false)
});
var releaseResultsDef = {
  name: "v1.autograde.releaseResults",
  module: "autograde",
  requestSchema: ReleaseResultsRequestSchema,
  responseSchema: ReleaseResultsResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  // domain dedupe on examId so a double-tap release flips the gate once.
  idempotencyKey: "domain:examId",
  // ⚷ flips resultsReleased + exam status + outbox notification (server authority).
  authoritySensitive: true,
  invalidates: ["exams", "submissions"]
};
var SaveEvaluationSettingsDataSchema = zObject({
  name: z.string().optional(),
  description: z.string().optional(),
  isDefault: z.boolean().optional(),
  isPublic: z.boolean().optional(),
  enabledDimensions: z.array(EvaluationDimensionSchema).optional(),
  displaySettings: EvaluationDisplaySettingsSchema.optional(),
  confidenceConfig: EvaluationConfidenceConfigSchema.optional(),
  usageQuota: UsageQuotaConfigSchema.optional()
});
var SaveEvaluationSettingsRequestSchema = zObject({
  id: zEvaluationSettingsId.optional(),
  data: SaveEvaluationSettingsDataSchema
});
var saveEvaluationSettingsDef = {
  name: "v1.autograde.saveEvaluationSettings",
  module: "autograde",
  requestSchema: SaveEvaluationSettingsRequestSchema,
  responseSchema: SaveEvaluationSettingsResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  idempotencyKey: "transport",
  // ⚷ confidence thresholds + promptGuidance are authoring-role-only secrets.
  authoritySensitive: true,
  invalidates: ["evaluationSettings"]
};
var RESOLVE_DEAD_LETTER_METHODS = ["retry", "manual_grade", "dismiss"];
var ResolveDeadLetterRequestSchema = zObject({
  entryId: zDeadLetterEntryId,
  method: z.enum(RESOLVE_DEAD_LETTER_METHODS)
});
var ResolveDeadLetterResponseSchema = zObject({
  success: z.boolean(),
  resolution: z.enum(DEAD_LETTER_RESOLUTION_METHODS)
});
var resolveDeadLetterDef = {
  name: "v1.autograde.resolveDeadLetter",
  module: "autograde",
  requestSchema: ResolveDeadLetterRequestSchema,
  responseSchema: ResolveDeadLetterResponseSchema,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  // domain dedupe on entryId so re-resolving a resolved entry is a no-op.
  idempotencyKey: "domain:entryId",
  // ⚷ re-enqueues a grading step / marks DLQ resolved (submission.grade authority).
  authoritySensitive: true,
  invalidates: ["gradingDeadLetter", "submissions"]
};
var ListExamsFilterSchema = zObject({
  status: zExamStatus.optional(),
  classId: zClassId.optional(),
  academicSessionId: zAcademicSessionId.optional(),
  subject: z.string().optional(),
  linkedSpaceId: zSpaceId.optional()
});
var ListExamsRequestSchema = zObject({
  ...zPageParamsShape,
  filter: ListExamsFilterSchema.optional()
});
var ListExamsResponseSchema = pageResponse(ExamListViewSchema);
var listExamsDef = {
  name: "v1.autograde.listExams",
  module: "autograde",
  requestSchema: ListExamsRequestSchema,
  responseSchema: ListExamsResponseSchema,
  authMode: "authed",
  rateTier: "read"
};
var GetExamRequestSchema = zObject({
  id: zExamId
});
var GetExamResponseSchema = ExamDetailViewSchema;
var getExamDef = {
  name: "v1.autograde.getExam",
  module: "autograde",
  requestSchema: GetExamRequestSchema,
  responseSchema: GetExamResponseSchema,
  authMode: "authed",
  rateTier: "read"
};
var ListQuestionsRequestSchema = zObject({
  examId: zExamId
});
var ListQuestionsResponseSchema = zObject({
  questions: z.array(ExamQuestionViewSchema)
});
var listQuestionsDef = {
  name: "v1.autograde.listQuestions",
  module: "autograde",
  requestSchema: ListQuestionsRequestSchema,
  responseSchema: ListQuestionsResponseSchema,
  authMode: "authed",
  rateTier: "read"
};
var ListSubmissionsFilterSchema = zObject({
  examId: zExamId,
  classId: zClassId.optional(),
  studentId: zStudentId.optional(),
  pipelineStatus: zSubmissionPipelineStatus.optional(),
  uploadedBy: zUserId.optional(),
  resultsReleasedOnly: z.boolean().optional()
});
var ListSubmissionsRequestSchema = zObject({
  ...zPageParamsShape,
  filter: ListSubmissionsFilterSchema
});
var ListSubmissionsResponseSchema = pageResponse(SubmissionListViewSchema);
var listSubmissionsDef = {
  name: "v1.autograde.listSubmissions",
  module: "autograde",
  requestSchema: ListSubmissionsRequestSchema,
  responseSchema: ListSubmissionsResponseSchema,
  authMode: "authed",
  rateTier: "read"
};
var GetSubmissionRequestSchema = zObject({
  id: zSubmissionId
});
var GetSubmissionResponseSchema = SubmissionDetailViewSchema;
var getSubmissionDef = {
  name: "v1.autograde.getSubmission",
  module: "autograde",
  requestSchema: GetSubmissionRequestSchema,
  responseSchema: GetSubmissionResponseSchema,
  authMode: "authed",
  rateTier: "read"
};
var ListQuestionSubmissionsRequestSchema = zObject({
  submissionId: zSubmissionId
});
var ListQuestionSubmissionsResponseSchema = zObject({
  questionSubmissions: z.array(QuestionSubmissionViewSchema)
});
var listQuestionSubmissionsDef = {
  name: "v1.autograde.listQuestionSubmissions",
  module: "autograde",
  requestSchema: ListQuestionSubmissionsRequestSchema,
  responseSchema: ListQuestionSubmissionsResponseSchema,
  authMode: "authed",
  rateTier: "read"
};
var GetExamAnalyticsRequestSchema = zObject({
  examId: zExamId
});
var GetExamAnalyticsResponseSchema = ExamAnalyticsViewSchema;
var getExamAnalyticsDef = {
  name: "v1.autograde.getExamAnalytics",
  module: "autograde",
  requestSchema: GetExamAnalyticsRequestSchema,
  responseSchema: GetExamAnalyticsResponseSchema,
  authMode: "authed",
  rateTier: "read"
};
var ListEvaluationSettingsRequestSchema = zObject({
  includePublic: z.boolean().optional()
});
var ListEvaluationSettingsResponseSchema = zObject({
  settings: z.array(EvaluationSettingsViewSchema)
});
var listEvaluationSettingsDef = {
  name: "v1.autograde.listEvaluationSettings",
  module: "autograde",
  requestSchema: ListEvaluationSettingsRequestSchema,
  responseSchema: ListEvaluationSettingsResponseSchema,
  authMode: "authed",
  rateTier: "read"
};
var AutogradeEvaluationConfigViewSchema = zObject({
  agent: AgentViewSchema.nullable(),
  rubric: UnifiedRubricSchema.nullable(),
  settings: EvaluationSettingsViewSchema.nullable(),
  provenance: EvaluationConfigProvenanceSchema
});
var GetAutogradeEvaluationConfigRequestSchema = zObject({
  examId: z.string(),
  /** Omit to preview the exam-level defaults (exam settings screen). */
  questionId: z.string().optional()
});
var GetAutogradeEvaluationConfigResponseSchema = zObject({
  config: AutogradeEvaluationConfigViewSchema
});
var getAutogradeEvaluationConfigDef = {
  name: "v1.autograde.getEvaluationConfig",
  module: "autograde",
  requestSchema: GetAutogradeEvaluationConfigRequestSchema,
  responseSchema: GetAutogradeEvaluationConfigResponseSchema,
  authMode: "authed",
  rateTier: "read"
};
var ListDeadLetterFilterSchema = zObject({
  resolved: z.boolean().optional(),
  pipelineStep: z.enum(GRADING_PIPELINE_STEPS).optional()
});
var ListDeadLetterRequestSchema = zObject({
  ...zPageParamsShape,
  filter: ListDeadLetterFilterSchema.optional()
});
var ListDeadLetterResponseSchema = pageResponse(DeadLetterViewSchema);
var listDeadLetterDef = {
  name: "v1.autograde.listDeadLetter",
  module: "autograde",
  requestSchema: ListDeadLetterRequestSchema,
  responseSchema: ListDeadLetterResponseSchema,
  authMode: "authed",
  rateTier: "read"
};

// src/callables/autograde/index.ts
var AUTOGRADE_CALLABLES = {
  "v1.autograde.saveExam": saveExamDef,
  "v1.autograde.saveExamQuestion": saveExamQuestionDef,
  "v1.autograde.extractQuestions": extractQuestionsDef,
  "v1.autograde.uploadAnswerSheets": uploadAnswerSheetsDef,
  "v1.autograde.gradeQuestion": gradeQuestionDef,
  "v1.autograde.releaseResults": releaseResultsDef,
  "v1.autograde.saveEvaluationSettings": saveEvaluationSettingsDef,
  "v1.autograde.resolveDeadLetter": resolveDeadLetterDef,
  "v1.autograde.listExams": listExamsDef,
  "v1.autograde.getExam": getExamDef,
  "v1.autograde.listQuestions": listQuestionsDef,
  "v1.autograde.listSubmissions": listSubmissionsDef,
  "v1.autograde.getSubmission": getSubmissionDef,
  "v1.autograde.listQuestionSubmissions": listQuestionSubmissionsDef,
  "v1.autograde.getExamAnalytics": getExamAnalyticsDef,
  "v1.autograde.listEvaluationSettings": listEvaluationSettingsDef,
  "v1.autograde.getEvaluationConfig": getAutogradeEvaluationConfigDef,
  "v1.autograde.listDeadLetter": listDeadLetterDef
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
};
var RequestUploadUrlRequestSchema = z.object({
  kind: z.enum([
    "answer-sheet",
    "question-paper",
    "content-source",
    "item-media",
    "answer-media"
  ]),
  /** Required for answer-sheet / question-paper kinds. */
  examId: z.string().optional(),
  /** Required for answer-sheet kind. */
  studentId: z.string().optional(),
  /** Required for answer-sheet kind (ownership scope gate). */
  classId: z.string().optional(),
  /** Required for content-source / item-media kinds. */
  spaceId: z.string().optional(),
  /** Required for item-media kind. */
  itemId: z.string().optional(),
  contentType: z.string()
}).strict();
var RequestUploadUrlResponseSchema = z.object({ uploadUrl: z.string(), path: z.string(), expiresAt: z.string() }).strict();
var requestUploadUrlDef = defineCallable({
  name: "v1.autograde.requestUploadUrl",
  module: "autograde",
  requestSchema: RequestUploadUrlRequestSchema,
  responseSchema: RequestUploadUrlResponseSchema,
  authMode: "authed",
  rateTier: "write"
  // No cache invalidation: a signed URL is a transient grant, never persisted state.
});
var GetSubmissionForExamRequestSchema = z.object({ examId: z.string(), studentId: z.string() }).strict();
var GetSubmissionForExamResponseSchema = SubmissionSchema.nullable();
var getSubmissionForExamDef = defineCallable({
  name: "v1.autograde.getSubmissionForExam",
  module: "autograde",
  requestSchema: GetSubmissionForExamRequestSchema,
  responseSchema: GetSubmissionForExamResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var AUTOGRADE_FOLD_CALLABLES = {
  "v1.autograde.requestUploadUrl": requestUploadUrlDef,
  "v1.autograde.getSubmissionForExam": getSubmissionForExamDef
};
var PerformanceTrendPointSchema = zObject({
  periodStart: zTimestamp,
  periodEnd: zTimestamp,
  avgPercentage: z.number(),
  examCount: z.number().int(),
  completionPct: z.number(),
  overallScore: z.number()
});
var TREND_GRANULARITIES = ["week", "month", "term"];
var zTrendGranularity = z.enum(TREND_GRANULARITIES);
var TimeRangeSchema = zObject({
  from: zTimestamp,
  to: zTimestamp
});
var PerClassRollupRowSchema = zObject({
  classId: zClassId,
  name: z.string(),
  avg: z.number(),
  atRisk: z.number().int()
});
var TenantRollupSchema = zObject({
  academyAvg: z.number(),
  perClass: z.array(PerClassRollupRowSchema)
});
var MasteryDistributionSchema = zObject({
  notStarted: z.number().int(),
  inProgress: z.number().int(),
  mastered: z.number().int()
});
var ClassSummaryViewSchema = zObject({
  ...ClassProgressSummarySchema.shape,
  tenantRollup: TenantRollupSchema,
  masteryDistribution: MasteryDistributionSchema
});
var PlatformKpisSchema = zObject({
  tenantCount: z.number().int(),
  userCount: z.number().int(),
  examCount: z.number().int(),
  activeTenantCount: z.number().int()
});
var GrowthSeriesPointSchema = zObject({
  date: zTimestamp,
  tenants: z.number().int(),
  users: z.number().int()
});
var TopTenantRowSchema = zObject({
  tenantId: zTenantId,
  name: z.string(),
  users: z.number().int(),
  exams: z.number().int()
});
var TenantComparisonRowSchema = zObject({
  tenantId: zTenantId,
  name: z.string(),
  users: z.number().int(),
  exams: z.number().int(),
  growthPct: z.number()
});
var PlatformSummarySchema = zObject({
  kpis: PlatformKpisSchema,
  growthSeries: z.array(GrowthSeriesPointSchema),
  planDistribution: z.record(z.string(), z.number().int()),
  topTenants: z.array(TopTenantRowSchema),
  tenantComparison: z.array(TenantComparisonRowSchema)
});
var HealthSummarySchema = zObject({
  snapshot: HealthSnapshotSchema
});
var PARENT_ALERT_KINDS = ["at_risk", "low_score", "low_streak"];
var zParentAlertKind = z.enum(PARENT_ALERT_KINDS);
var ParentAlertSchema = zObject({
  studentId: zStudentId,
  name: z.string(),
  kind: zParentAlertKind,
  detail: z.string(),
  createdAt: zTimestamp
});
var LinkedChildRowSchema = zObject({
  studentId: zStudentId,
  name: z.string(),
  classNames: z.array(z.string()),
  overallScore: z.number(),
  isAtRisk: z.boolean(),
  atRiskReasons: z.array(zAtRiskReason).default([])
});
var ASSIGNMENT_MATRIX_STATUSES = [
  "not_started",
  "in_progress",
  "completed",
  "overdue"
];
var zAssignmentMatrixStatus = z.enum(ASSIGNMENT_MATRIX_STATUSES);
var AssignmentMatrixCellSchema = zObject({
  studentId: zStudentId,
  status: zAssignmentMatrixStatus,
  completionPct: z.number()
});
var AssignmentMatrixRowSchema = zObject({
  contentId: z.string(),
  contentTitle: z.string(),
  contentType: z.enum(["space", "exam"]),
  dueAt: zTimestamp.nullable(),
  cells: z.array(AssignmentMatrixCellSchema)
});
var AssignmentMatrixSchema = zObject({
  classId: zClassId,
  students: z.array(zObject({ studentId: zStudentId, name: z.string() })),
  rows: z.array(AssignmentMatrixRowSchema)
});
var GetSummaryRequestSchema = z.discriminatedUnion("scope", [
  zObject({ scope: z.literal("student"), studentId: zStudentId.optional() }),
  zObject({ scope: z.literal("class"), classId: zClassId.optional() }),
  zObject({ scope: z.literal("platform") }),
  zObject({ scope: z.literal("health") })
]);
var GetSummaryResponseSchema = z.discriminatedUnion("scope", [
  zObject({ scope: z.literal("student"), studentSummary: StudentProgressSummarySchema }),
  zObject({ scope: z.literal("class"), classSummary: ClassSummaryViewSchema }),
  zObject({ scope: z.literal("platform"), platformSummary: PlatformSummarySchema }),
  zObject({ scope: z.literal("health"), healthSummary: HealthSummarySchema })
]);
var getSummary = defineCallable({
  name: "v1.analytics.getSummary",
  module: "analytics",
  requestSchema: GetSummaryRequestSchema,
  responseSchema: GetSummaryResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var GenerateReportRequestSchema = zObject({
  type: z.enum(["exam-result", "progress", "class"]),
  examId: zExamId.optional(),
  studentId: zStudentId.optional(),
  classId: zClassId.optional()
});
var GenerateReportResponseSchema = zObject({
  pdfUrl: z.string(),
  expiresAt: zTimestamp
});
var generateReport = defineCallable({
  name: "v1.analytics.generateReport",
  module: "analytics",
  requestSchema: GenerateReportRequestSchema,
  responseSchema: GenerateReportResponseSchema,
  authMode: "authed",
  rateTier: "report",
  idempotent: true
});
var GetExamAnalyticsRequestSchema2 = zObject({
  examId: zExamId
});
var GetExamAnalyticsResponseSchema2 = ExamAnalyticsSchema;
var getExamAnalytics = defineCallable({
  name: "v1.analytics.getExamAnalytics",
  module: "analytics",
  requestSchema: GetExamAnalyticsRequestSchema2,
  responseSchema: GetExamAnalyticsResponseSchema2,
  authMode: "authed",
  rateTier: "read"
});
var ListInsightsRequestSchema = zObject({
  studentId: zStudentId,
  includeDismissed: z.boolean().optional(),
  ...PageRequest.shape
});
var ListInsightsResponseSchema = pageResponse(LearningInsightSchema);
var listInsights = defineCallable({
  name: "v1.analytics.listInsights",
  module: "analytics",
  requestSchema: ListInsightsRequestSchema,
  responseSchema: ListInsightsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var DismissInsightRequestSchema2 = zObject({
  insightId: zInsightId
});
var DismissInsightResponseSchema2 = zObject({
  id: zInsightId,
  dismissedAt: zTimestamp
});
var dismissInsight = defineCallable({
  name: "v1.analytics.dismissInsight",
  module: "analytics",
  requestSchema: DismissInsightRequestSchema2,
  responseSchema: DismissInsightResponseSchema2,
  authMode: "authed",
  rateTier: "write",
  idempotent: true,
  invalidates: ["insights"]
});
var GetPerformanceTrendsRequestSchema = zObject({
  subjectId: z.string().optional(),
  studentId: zStudentId.optional(),
  classId: zClassId.optional(),
  granularity: zTrendGranularity,
  range: TimeRangeSchema.optional()
});
var GetPerformanceTrendsResponseSchema = zObject({
  points: z.array(PerformanceTrendPointSchema)
});
var getPerformanceTrends = defineCallable({
  name: "v1.analytics.getPerformanceTrends",
  module: "analytics",
  requestSchema: GetPerformanceTrendsRequestSchema,
  responseSchema: GetPerformanceTrendsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var GetChildSummaryRequestSchema = zObject({
  studentId: zStudentId
});
var GetChildSummaryResponseSchema = zObject({
  studentSummary: StudentProgressSummarySchema,
  recentInsights: z.array(LearningInsightSchema)
});
var getChildSummary = defineCallable({
  name: "v1.analytics.getChildSummary",
  module: "analytics",
  requestSchema: GetChildSummaryRequestSchema,
  responseSchema: GetChildSummaryResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListLinkedChildrenRequestSchema = PageRequest;
var ListLinkedChildrenResponseSchema = pageResponse(LinkedChildRowSchema);
var listLinkedChildren = defineCallable({
  name: "v1.analytics.listLinkedChildren",
  module: "analytics",
  requestSchema: ListLinkedChildrenRequestSchema,
  responseSchema: ListLinkedChildrenResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var GetCostSummaryRequestSchema = zObject({
  granularity: zCostSummaryGranularity,
  // exact-day (daily) selector
  date: zIsoDate.optional(),
  // exact-month (monthly) selector, YYYY-MM
  month: z.string().regex(/^\d{4}-\d{2}$/).optional(),
  range: TimeRangeSchema.optional(),
  /** Super-admin cross-tenant read (platform LLM-usage roll-up). */
  tenantOverride: zTenantId.optional()
});
var GetCostSummaryResponseSchema = zObject({
  summaries: z.array(z.union([DailyCostSummarySchema, MonthlyCostSummarySchema]))
});
var getCostSummary = defineCallable({
  name: "v1.analytics.getCostSummary",
  module: "analytics",
  requestSchema: GetCostSummaryRequestSchema,
  responseSchema: GetCostSummaryResponseSchema,
  authMode: "authed",
  rateTier: "read",
  allowsTenantOverride: true
});
var LEADERBOARD_SCOPES = ["tenant", "space", "storyPoint"];
var zLeaderboardScope = z.enum(LEADERBOARD_SCOPES);
var GetLeaderboardRequestSchema2 = zObject({
  scope: zLeaderboardScope,
  spaceId: zSpaceId.optional(),
  storyPointId: zStoryPointId.optional(),
  limit: z.number().int().min(1).max(100).optional()
});
var GetLeaderboardResponseSchema2 = zObject({
  entries: z.array(LeaderboardEntrySchema),
  myEntry: LeaderboardEntrySchema.optional()
});
var getLeaderboard = defineCallable({
  name: "v1.analytics.getLeaderboard",
  module: "analytics",
  requestSchema: GetLeaderboardRequestSchema2,
  responseSchema: GetLeaderboardResponseSchema2,
  authMode: "authed",
  rateTier: "read"
});
var ListParentAlertsRequestSchema = PageRequest;
var ListParentAlertsResponseSchema = pageResponse(ParentAlertSchema);
var listParentAlerts = defineCallable({
  name: "v1.analytics.listParentAlerts",
  module: "analytics",
  requestSchema: ListParentAlertsRequestSchema,
  responseSchema: ListParentAlertsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var ListPlatformActivityRequestSchema = zObject({
  action: zPlatformActivityAction.optional(),
  tenantOverride: zTenantId.optional(),
  ...PageRequest.shape
});
var ListPlatformActivityResponseSchema = pageResponse(PlatformActivityLogSchema);
var listPlatformActivity = defineCallable({
  name: "v1.analytics.listPlatformActivity",
  module: "analytics",
  requestSchema: ListPlatformActivityRequestSchema,
  responseSchema: ListPlatformActivityResponseSchema,
  authMode: "authed",
  rateTier: "read",
  allowsTenantOverride: true
});
var GetAssignmentMatrixRequestSchema = zObject({
  classId: zClassId
});
var GetAssignmentMatrixResponseSchema = AssignmentMatrixSchema;
var getAssignmentMatrix = defineCallable({
  name: "v1.analytics.getAssignmentMatrix",
  module: "analytics",
  requestSchema: GetAssignmentMatrixRequestSchema,
  responseSchema: GetAssignmentMatrixResponseSchema,
  authMode: "authed",
  rateTier: "read"
});
var SpaceAnalyticsStatusSchema = z.enum(["not_started", "in_progress", "completed"]);
var SpaceAnalyticsStudentSchema = zObject({
  studentId: zStudentId,
  name: z.string(),
  classIds: z.array(z.string()),
  status: SpaceAnalyticsStatusSchema,
  completionPct: z.number().min(0).max(100),
  completedItems: z.number().int().nonnegative(),
  totalItems: z.number().int().nonnegative(),
  pointsEarned: z.number().nonnegative(),
  totalPoints: z.number().nonnegative(),
  timeSpentSeconds: z.number().nonnegative(),
  attempts: z.number().int().nonnegative(),
  lastActivityAt: zTimestamp.nullable()
});
var SpaceAnalyticsSummarySchema = zObject({
  totalStudents: z.number().int().nonnegative(),
  startedStudents: z.number().int().nonnegative(),
  completedStudents: z.number().int().nonnegative(),
  activeStudents7d: z.number().int().nonnegative(),
  avgCompletionPct: z.number().min(0).max(100),
  avgTimeSpentSeconds: z.number().nonnegative(),
  totalAttempts: z.number().int().nonnegative()
});
var GetSpaceAnalyticsRequestSchema = zObject({
  spaceId: zSpaceId
});
var GetSpaceAnalyticsResponseSchema = zObject({
  spaceId: zSpaceId,
  generatedAt: zTimestamp,
  summary: SpaceAnalyticsSummarySchema,
  students: z.array(SpaceAnalyticsStudentSchema)
});
var getSpaceAnalytics = defineCallable({
  name: "v1.analytics.getSpaceAnalytics",
  module: "analytics",
  requestSchema: GetSpaceAnalyticsRequestSchema,
  responseSchema: GetSpaceAnalyticsResponseSchema,
  authMode: "authed",
  rateTier: "read"
});

// src/callables/analytics/index.ts
var ANALYTICS_CALLABLES = {
  "v1.analytics.getSummary": getSummary,
  "v1.analytics.generateReport": generateReport,
  "v1.analytics.getExamAnalytics": getExamAnalytics,
  "v1.analytics.listInsights": listInsights,
  "v1.analytics.dismissInsight": dismissInsight,
  "v1.analytics.getPerformanceTrends": getPerformanceTrends,
  "v1.analytics.getChildSummary": getChildSummary,
  "v1.analytics.listLinkedChildren": listLinkedChildren,
  "v1.analytics.getCostSummary": getCostSummary,
  "v1.analytics.getLeaderboard": getLeaderboard,
  "v1.analytics.listParentAlerts": listParentAlerts,
  "v1.analytics.listPlatformActivity": listPlatformActivity,
  "v1.analytics.getAssignmentMatrix": getAssignmentMatrix,
  "v1.analytics.getSpaceAnalytics": getSpaceAnalytics
};

// src/registry.ts
var CALLABLES = {
  ...IDENTITY_CALLABLES,
  ...LEVELUP_CONTENT_CALLABLES,
  ...GAMIFICATION_CALLABLES,
  ...AUTOGRADE_CALLABLES,
  ...AUTOGRADE_FOLD_CALLABLES,
  ...ANALYTICS_CALLABLES
};
var CALLABLE_NAMES = Object.keys(CALLABLES);
function getCallable(name) {
  const def = CALLABLES[name];
  if (!def) throw new Error(`[api-contract] unknown callable: ${name}`);
  return def;
}
function callablesForModule(module) {
  return CALLABLE_NAMES.map((n) => CALLABLES[n]).filter(
    (d) => d.module === module
  );
}
var AUTHORITY_CALLABLES = Object.freeze(
  Object.values(CALLABLES).filter((d) => d.authoritySensitive).map((d) => d.name)
);
var OPTIMISTIC_ALLOWLIST = [
  "v1.levelup.recordItemAttempt",
  "v1.levelup.sendChatMessage",
  "v1.identity.markNotificationRead",
  "v1.identity.markAnnouncementRead",
  "v1.levelup.dismissInsight",
  "v1.analytics.dismissInsight",
  "v1.levelup.markAchievementsSeen"
];
var OPTIMISTIC_COUNTER_ALLOWLIST = ["unreadCount", "unseenCount"];

// src/domains.ts
var DOMAIN_NAMES = [
  // identity
  "me",
  "tenants",
  "students",
  "teachers",
  "parents",
  "staff",
  "classes",
  "sessions",
  "announcements",
  "notifications",
  "notificationBadge",
  "userSearch",
  "preset",
  "platformConfig",
  "notificationPreferences",
  "device",
  "message",
  "exportJob",
  "memberships",
  "claims",
  "academicSessions",
  // levelup (content + testsession + gamification)
  "spaces",
  "storyPoints",
  "items",
  "versions",
  "questionBank",
  "rubricPresets",
  "agents",
  "chat",
  "conversations",
  "store",
  "reviews",
  "enrollment",
  "enrollments",
  "progress",
  "testSessions",
  "insights",
  "leaderboard",
  "gamification",
  "achievements",
  "levels",
  "studyGoals",
  "studySessions",
  "studentSummary",
  "assignment",
  "aiGeneration",
  "storage",
  // autograde
  "exams",
  "questions",
  "submissions",
  "questionSubmissions",
  "evalSettings",
  "evaluationSettings",
  "deadLetter",
  "gradingDeadLetter",
  "examAnalytics",
  "gradingReview",
  // analytics
  "summary",
  "trends",
  "cost",
  "analytics",
  "parentAlert",
  "platformActivity"
];
var DOMAIN_NAME_SET = new Set(DOMAIN_NAMES);
var INVALIDATION_GRAPH = Object.freeze(
  Object.fromEntries(
    Object.values(CALLABLES).filter((d) => d.invalidates && d.invalidates.length > 0).map((d) => {
      const roots = (d.invalidates ?? []).filter((r) => DOMAIN_NAME_SET.has(r));
      return [d.name, { roots }];
    })
  )
);
function unknownInvalidationRoots() {
  const bad = [];
  for (const d of Object.values(CALLABLES)) {
    for (const r of d.invalidates ?? []) {
      if (!DOMAIN_NAME_SET.has(r)) bad.push(`${d.name} \u2192 ${r}`);
    }
  }
  return bad;
}
var APP_ERROR_CODES = [
  "VALIDATION_ERROR",
  "INVALID_TRANSITION",
  "NOT_FOUND",
  "PERMISSION_DENIED",
  "UNAUTHENTICATED",
  "RATE_LIMITED",
  "QUOTA_EXCEEDED",
  "FEATURE_DISABLED",
  "TENANT_SUSPENDED",
  "CONFLICT",
  "PRECONDITION_FAILED",
  "IDEMPOTENCY_CONFLICT",
  "PAYMENT_FAILED",
  "INTERNAL_ERROR"
];
var DEFAULT_RETRYABLE = {
  VALIDATION_ERROR: false,
  INVALID_TRANSITION: false,
  NOT_FOUND: false,
  PERMISSION_DENIED: false,
  UNAUTHENTICATED: false,
  RATE_LIMITED: true,
  QUOTA_EXCEEDED: false,
  FEATURE_DISABLED: false,
  TENANT_SUSPENDED: false,
  CONFLICT: true,
  PRECONDITION_FAILED: false,
  IDEMPOTENCY_CONFLICT: true,
  PAYMENT_FAILED: false,
  INTERNAL_ERROR: true
};
var ApiErrorDetailsSchema = z.object({
  code: z.enum(APP_ERROR_CODES),
  message: z.string(),
  validationErrors: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
  retryable: z.boolean().optional(),
  meta: z.record(z.string(), z.any()).optional()
}).strict();
function isApiErrorDetails(x) {
  return ApiErrorDetailsSchema.safeParse(x).success;
}
var APP_ERROR_TO_HTTPS = {
  VALIDATION_ERROR: "invalid-argument",
  INVALID_TRANSITION: "failed-precondition",
  NOT_FOUND: "not-found",
  PERMISSION_DENIED: "permission-denied",
  UNAUTHENTICATED: "unauthenticated",
  RATE_LIMITED: "resource-exhausted",
  QUOTA_EXCEEDED: "resource-exhausted",
  FEATURE_DISABLED: "failed-precondition",
  TENANT_SUSPENDED: "failed-precondition",
  CONFLICT: "already-exists",
  PRECONDITION_FAILED: "failed-precondition",
  IDEMPOTENCY_CONFLICT: "already-exists",
  PAYMENT_FAILED: "failed-precondition",
  INTERNAL_ERROR: "internal"
};
var HTTPS_TO_APP_ERROR = {
  ok: "INTERNAL_ERROR",
  cancelled: "INTERNAL_ERROR",
  unknown: "INTERNAL_ERROR",
  "invalid-argument": "VALIDATION_ERROR",
  "deadline-exceeded": "INTERNAL_ERROR",
  "not-found": "NOT_FOUND",
  "already-exists": "CONFLICT",
  "permission-denied": "PERMISSION_DENIED",
  "resource-exhausted": "RATE_LIMITED",
  "failed-precondition": "PRECONDITION_FAILED",
  aborted: "CONFLICT",
  "out-of-range": "VALIDATION_ERROR",
  unimplemented: "INTERNAL_ERROR",
  internal: "INTERNAL_ERROR",
  unavailable: "INTERNAL_ERROR",
  "data-loss": "INTERNAL_ERROR",
  unauthenticated: "UNAUTHENTICATED"
};
var ERROR_MESSAGES = {
  VALIDATION_ERROR: "The request contains invalid data.",
  INVALID_TRANSITION: "That action is not allowed from the current state.",
  NOT_FOUND: "The requested resource was not found.",
  PERMISSION_DENIED: "You do not have permission to perform this action.",
  UNAUTHENTICATED: "You must be signed in to perform this action.",
  RATE_LIMITED: "Too many requests. Please try again in a moment.",
  QUOTA_EXCEEDED: "You have exceeded your usage quota.",
  FEATURE_DISABLED: "This feature is not enabled for your organization.",
  TENANT_SUSPENDED: "Your organization account is currently suspended.",
  CONFLICT: "This resource was modified elsewhere. Refresh and retry.",
  PRECONDITION_FAILED: "The operation cannot be performed in the current state.",
  IDEMPOTENCY_CONFLICT: "A conflicting request with the same key is already in progress.",
  PAYMENT_FAILED: "The payment could not be completed.",
  INTERNAL_ERROR: "An unexpected error occurred. Please try again."
};
var ERROR_RECOVERY_HINTS = {
  VALIDATION_ERROR: "Check the highlighted fields and try again.",
  INVALID_TRANSITION: "Refresh to see the current status, then retry.",
  NOT_FOUND: "The item may have been deleted. Try refreshing.",
  PERMISSION_DENIED: "Contact your administrator if you need access.",
  UNAUTHENTICATED: "Please sign in and try again.",
  RATE_LIMITED: "Wait a few seconds before trying again.",
  QUOTA_EXCEEDED: "Contact your administrator to upgrade your plan.",
  FEATURE_DISABLED: "Ask your administrator to enable this feature.",
  TENANT_SUSPENDED: "Contact support to restore your account.",
  CONFLICT: "Refresh the page to load the latest version.",
  PRECONDITION_FAILED: "Refresh the page and verify the current status.",
  IDEMPOTENCY_CONFLICT: "Wait for the in-flight request to finish.",
  PAYMENT_FAILED: "Check your payment details and try again.",
  INTERNAL_ERROR: "If the problem persists, contact support."
};

// src/subscriptions/subscription-def.ts
function defineSubscription(def) {
  return def;
}
var TestSessionLiveSchema = zObject({
  /** Server-computed milliseconds remaining; clamped to 0 at/after the deadline. */
  remainingMs: z.number().int().min(0),
  /** Authoritative deadline as an ISO-8601 string (the client never owns the clock). */
  serverDeadline: z.string(),
  status: zTestSessionStatus
});
var TestSessionDeadlineParamsSchema = zObject({ sessionId: z.string() });
var testSessionDeadline = defineSubscription({
  name: "v1.levelup.testSessionDeadline",
  module: "levelup",
  source: "rtdb-node",
  params: TestSessionDeadlineParamsSchema,
  payload: TestSessionLiveSchema
});
var ChatStreamParamsSchema = zObject({ sessionId: z.string() });
var ChatBumpSchema = zObject({
  /** Monotonic per-session revision (server-side atomic increment). */
  rev: z.number(),
  /** ISO timestamp of the message that triggered the bump. */
  lastMessageAt: z.string()
});
var chatStream = defineSubscription({
  name: "v1.levelup.chatStream",
  module: "levelup",
  source: "rtdb-node",
  params: ChatStreamParamsSchema,
  payload: ChatBumpSchema
});
var StoryPointProgressLiveSchema = zObject({
  storyPointId: z.string(),
  status: zProgressStatus,
  pointsEarned: z.number(),
  totalPoints: z.number(),
  percentage: z.number()
});
var SpaceProgressLiveSchema = zObject({
  spaceId: z.string(),
  userId: z.string(),
  status: zProgressStatus,
  pointsEarned: z.number(),
  totalPoints: z.number(),
  percentage: z.number(),
  // RTDB drops empty objects at rest — an entry-less rollup arrives keyless.
  storyPoints: z.record(z.string(), StoryPointProgressLiveSchema).default({}),
  updatedAt: z.string()
});
var SpaceProgressLiveParamsSchema = zObject({
  spaceId: z.string(),
  userId: z.string()
});
var spaceProgressLive = defineSubscription({
  name: "v1.levelup.spaceProgressLive",
  module: "levelup",
  source: "rtdb-node",
  params: SpaceProgressLiveParamsSchema,
  payload: SpaceProgressLiveSchema
});
var LeaderboardSnapshotSchema = zObject({
  entries: z.array(LeaderboardEntrySchema),
  callerRank: z.number().int().nullable()
});
var LeaderboardLiveParamsSchema = zObject({
  scope: z.enum(["tenant", "class", "space", "storyPoint"]),
  spaceId: z.string().optional(),
  storyPointId: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50)
});
var leaderboardLive = defineSubscription({
  name: "v1.levelup.leaderboardLive",
  module: "analytics",
  source: "rtdb-node",
  params: LeaderboardLiveParamsSchema,
  payload: LeaderboardSnapshotSchema
});
var StudentLevelLiveParamsSchema = zObject({});
var studentLevelLive = defineSubscription({
  name: "v1.levelup.studentLevelLive",
  module: "levelup",
  source: "rtdb-node",
  params: StudentLevelLiveParamsSchema,
  payload: StudentLevelSchema
});
var AchievementUnlockSchema = StudentAchievementSchema;
var AchievementUnlockParamsSchema = zObject({});
var achievementUnlock = defineSubscription({
  name: "v1.levelup.achievementUnlock",
  module: "levelup",
  source: "rtdb-node",
  params: AchievementUnlockParamsSchema,
  payload: AchievementUnlockSchema
});
var SubmissionStatusSchema = zObject({
  pipelineStatus: zSubmissionPipelineStatus,
  gradingProgress: GradingProgressSchema.optional(),
  updatedAt: z.string()
});
var GradingStatusParamsSchema = zObject({ submissionId: z.string() });
var gradingStatus = defineSubscription({
  name: "v1.autograde.gradingStatus",
  module: "autograde",
  source: "rtdb-node",
  params: GradingStatusParamsSchema,
  payload: SubmissionStatusSchema
});
var ExamGradingProgressSchema = zObject({
  examId: z.string(),
  totalSubmissions: z.number().int().min(0),
  gradedSubmissions: z.number().int().min(0),
  failedSubmissions: z.number().int().min(0),
  pendingSubmissions: z.number().int().min(0),
  /** Coarse exam-wide phase (reuses the per-submission pipeline vocabulary). */
  phase: zSubmissionPipelineStatus.optional(),
  updatedAt: z.string()
});
var ExamGradingParamsSchema = zObject({ examId: z.string() });
var examGrading = defineSubscription({
  name: "v1.autograde.examGrading",
  module: "autograde",
  source: "rtdb-node",
  params: ExamGradingParamsSchema,
  payload: ExamGradingProgressSchema
});
var EXTRACTION_PHASES = [
  "extracting_questions",
  "questions_extracted",
  "generating_rubrics",
  "complete",
  "failed"
];
var ExtractionStatusSchema = zObject({
  examId: zExamId,
  phase: z.enum(EXTRACTION_PHASES),
  /** 0 until Pass 1 lands; then the total question count. */
  totalQuestions: z.number().int().min(0),
  /** How many questions have a generated rubric so far. */
  rubricsGenerated: z.number().int().min(0),
  mode: z.enum(["full", "single", "rubrics"]).optional(),
  /** Present only on `failed` — a message, never a stack. */
  error: z.string().optional(),
  failedPhase: z.enum(["questions", "rubrics"]).optional(),
  updatedAt: z.string()
});
var ExtractionStatusParamsSchema = zObject({ examId: zExamId });
var extractionStatus = defineSubscription({
  name: "v1.autograde.extractionStatus",
  module: "autograde",
  source: "rtdb-node",
  params: ExtractionStatusParamsSchema,
  payload: ExtractionStatusSchema
});
var NotificationStateSchema = NotificationBadgeStateSchema;
var NotificationBadgeParamsSchema = zObject({});
var notificationBadge = defineSubscription({
  name: "v1.notification.badge",
  module: "identity",
  source: "rtdb-node",
  params: NotificationBadgeParamsSchema,
  payload: NotificationStateSchema
});

// src/callables/subscriptions/index.ts
var SUBSCRIPTION_DEFS = {
  "v1.levelup.testSessionDeadline": testSessionDeadline,
  "v1.levelup.chatStream": chatStream,
  "v1.levelup.spaceProgressLive": spaceProgressLive,
  "v1.levelup.leaderboardLive": leaderboardLive,
  "v1.levelup.studentLevelLive": studentLevelLive,
  "v1.levelup.achievementUnlock": achievementUnlock,
  "v1.autograde.gradingStatus": gradingStatus,
  "v1.autograde.examGrading": examGrading,
  "v1.autograde.extractionStatus": extractionStatus,
  "v1.notification.badge": notificationBadge
};

// src/subscriptions/registry.ts
var SUBSCRIPTIONS = {
  ...SUBSCRIPTION_DEFS
};
var SUBSCRIPTION_NAMES = Object.keys(SUBSCRIPTIONS);

// src/meta.ts
var API_VERSION = "v1";
var callableName = (module, op) => `${API_VERSION}.${module}.${op}`;
function parseCallableName(name) {
  const m = /^(v\d+)\.([a-z]+)\.([A-Za-z]+)$/.exec(name);
  return m ? { version: m[1], module: m[2], op: m[3] } : null;
}
var RATE_LIMITS = {
  write: { maxPerMinute: 30, actionType: "write" },
  read: { maxPerMinute: 60, actionType: "read" },
  ai: { maxPerMinute: 10, actionType: "ai" },
  auth: { maxPerMinute: 10, actionType: "auth" },
  report: { maxPerMinute: 5, actionType: "report" }
};

export { API_MODULES, API_VERSION, APP_ERROR_CODES, APP_ERROR_TO_HTTPS, AUTHORITY_CALLABLES, AbandonConversationRequestSchema, AbandonConversationResponseSchema, AchievementUnlockParamsSchema, AchievementUnlockSchema, ApiErrorDetailsSchema, CALLABLES, CALLABLE_NAMES, ChatBumpSchema, ChatStreamParamsSchema, ConversationMediaInputSchema, DEFAULT_RETRYABLE, DOMAIN_NAMES, ERROR_MESSAGES, ERROR_RECOVERY_HINTS, EXTRACTION_PHASES, ExamGradingParamsSchema, ExamGradingProgressSchema, ExtractionStatusParamsSchema, ExtractionStatusSchema, FinishConversationRequestSchema, FinishConversationResponseSchema, FinishConversationResultSchema, GeneratedItemSchema, GetConversationRequestSchema, GetConversationResponseSchema, GradingStatusParamsSchema, HTTPS_TO_APP_ERROR, INVALIDATION_GRAPH, LeaderboardLiveParamsSchema, LeaderboardSnapshotSchema, ListConversationsRequestSchema, ListConversationsResponseSchema, NotificationBadgeParamsSchema, NotificationStateSchema, OPTIMISTIC_ALLOWLIST, OPTIMISTIC_COUNTER_ALLOWLIST, PageRequest, QuestionHelpDraftSnapshotSchema, RATE_LIMITS, RATE_TIERS, SUBSCRIPTIONS, SUBSCRIPTION_DEFS, SUBSCRIPTION_NAMES, SaveResponseSchema, SendConversationTurnInputSchema, SendConversationTurnRequestSchema, SendConversationTurnResponseSchema, SpaceProgressLiveParamsSchema, SpaceProgressLiveSchema, StartConversationRequestSchema, StartConversationResponseSchema, StoryPointProgressLiveSchema, StudentLevelLiveParamsSchema, SubmissionStatusSchema, TestSessionDeadlineParamsSchema, TestSessionLiveSchema, abandonConversationDef, achievementUnlock, callableName, callablesForModule, chatStream, defineCallable, defineSubscription, examGrading, extractionStatus, finishConversationDef, getCallable, getConversationDef, gradingStatus, isApiErrorDetails, leaderboardLive, listConversationsDef, notificationBadge, pageResponse, parseCallableName, sendConversationTurnDef, spaceProgressLive, startConversationDef, studentLevelLive, testSessionDeadline, unknownInvalidationRoots, withPaging };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map